import { createClient } from "@supabase/supabase-js";
import {
  Profile,
  Post,
  Book,
  Chapter,
  Notification,
  Comment,
  Competition,
  CompetitionEntry,
  PayoutTransaction,
  BankDetails,
  cleanAuthorName,
} from "./types";

// Environment Variables for Supabase
// Fix: Use type-safe access to environment variables.
// Vite's 'define' will replace these with actual values from process.env during build/dev.
let supabaseUrl =
  (import.meta as any).env?.VITE_SUPABASE_URL ||
  (typeof process !== "undefined" ? process.env.VITE_SUPABASE_URL : "") ||
  (typeof process !== "undefined" ? process.env.SUPABASE_URL : "");
let supabaseAnonKey =
  (import.meta as any).env?.VITE_SUPABASE_ANON_KEY ||
  (typeof process !== "undefined" ? process.env.VITE_SUPABASE_ANON_KEY : "") ||
  (typeof process !== "undefined" ? process.env.SUPABASE_ANON_KEY : "") ||
  (import.meta as any).env?.VITE_SUPABASE_PUBLISHABLE_KEY ||
  (typeof process !== "undefined" ? process.env.SUPABASE_PUBLISHABLE_KEY : "") ||
  (import.meta as any).env?.VITE_SUPABASE_PUBLISHABLE_DEFAULT_KEY ||
  (typeof process !== "undefined"
    ? process.env.VITE_SUPABASE_PUBLISHABLE_DEFAULT_KEY
    : "") ||
  "";

if (supabaseUrl) {
  supabaseUrl = supabaseUrl.trim().replace(/\/$/, ""); // Remove trailing slash
  if (!supabaseUrl.startsWith("http")) {
    supabaseUrl = `https://${supabaseUrl}`;
  }
}

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    "Supabase configuration missing. Please check your environment variables (VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY).",
  );
}

// Initialize Supabase Client (if keys are provided)
if (supabaseUrl && supabaseAnonKey) {
  console.log("Supabase initialized with URL:", supabaseUrl);
  // Check if URL looks like it's pointing to the app itself instead of Supabase
  if (
    supabaseUrl.includes(".run.app") &&
    !supabaseUrl.includes("supabase.co")
  ) {
    console.warn(
      "CRITICAL WARNING: VITE_SUPABASE_URL appears to be pointing to the app's own URL instead of a Supabase project. This will cause authentication and database operations to hang or timeout. Disabling Supabase.",
    );
    supabaseUrl = "";
    supabaseAnonKey = "";
  }
}

export let isRlsMisconfigured = false;
export let supabase: any =
  supabaseUrl && supabaseAnonKey && supabaseUrl !== ""
    ? createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
          storageKey: "literally-auth-token",
        },
      })
    : null;

// Cache for the current user to prevent concurrent getUser calls
let currentUserCache: any = null;
let currentUserPromise: Promise<any> | null = null;

// Cache for profile fetches to prevent concurrent calls for same user
const profileFetchPromises: Record<
  string,
  Promise<Profile | null> | undefined
> = {};

const resolvedProfileCache: Record<string, Profile> = {};
const serviceCache: Record<string, any> = {};

if (typeof window !== "undefined") {
  window.addEventListener("profileUpdated", (e: any) => {
    if (e.detail && e.detail.id) {
      resolvedProfileCache[e.detail.id] = e.detail;
    }
  });
}

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

const getMissingCompetitionColumn = (error: { code?: string; message?: string } | null) => {
  const message = String(error?.message || "");
  if (
    error?.code !== "PGRST204" &&
    error?.code !== "42703" &&
    !/could not find the .* column .* schema cache|column .* does not exist/i.test(message)
  ) {
    return null;
  }

  return (
    message.match(/the ['"]([^'"]+)['"] column of ['"]competitions['"]/i)?.[1] ||
    message.match(/column ['"]?([a-z_][a-z0-9_]*)['"]? of relation ['"]competitions['"]/i)?.[1] ||
    message.match(/column competitions\.([a-z_][a-z0-9_]*) does not exist/i)?.[1] ||
    null
  );
};

const getRealId = (id: string): string => {
  if (!id || typeof id !== "string") return "";
  const match = id.match(UUID_PATTERN);
  if (match) {
    return match[0];
  }
  if (id.includes("-")) {
    const parts = id.split("-");
    const lastPart = parts[parts.length - 1];
    if (/^\d+$/.test(lastPart)) {
      return parts.slice(0, parts.length - 1).join("-");
    }
  }
  return id;
};

const getCurrentUserInternal = async () => {
  if (!supabase) return null;

  // If we have a cached user, return it
  if (currentUserCache) return currentUserCache;

  // If a request is already in progress, wait for it
  if (currentUserPromise) return currentUserPromise;

  console.log("getCurrentUserInternal: Fetching user...");

  currentUserPromise = (async () => {
    try {
      console.log(
        "getCurrentUserInternal: Calling supabase.auth.getSession()...",
      );

      // Add a timeout to the auth call
      const authPromise = supabase.auth.getSession();
      
      let timeoutId: any;
      const timeoutPromise = new Promise<{ data: { session: null }; error: any }>((resolve) => {
        timeoutId = setTimeout(() => {
          resolve({ data: { session: null }, error: new Error("Auth getSession timeout") });
        }, 8000); // 8 seconds is plenty for session check
      });

      const result = (await Promise.race([
        authPromise.then(
          (val: any) => {
            clearTimeout(timeoutId);
            return val;
          },
          (err: any) => {
            clearTimeout(timeoutId);
            return { data: { session: null }, error: err };
          }
        ),
        timeoutPromise
      ])) as any;

      const {
        data: { session },
        error,
      } = result;

      if (error) {
        console.warn("getCurrentUserInternal: Supabase error or timeout:", error.message);
        
        

        // Handle invalid refresh token by signing out to clear local state
        if (
          error.message && (
            error.message.includes("Refresh Token Not Found") ||
            error.message.includes("invalid_grant") ||
            error.message.includes("refresh_token_not_found")
          )
        ) {
          console.log(
            "getCurrentUserInternal: Invalid session detected, clearing auth state...",
          );
          // Attempt signing out to purge the broken session
          try {
            await supabase.auth.signOut();
            localStorage.removeItem("literally-auth-token");
          } catch (e) {
            console.warn("Failed to sign out during session cleanup", e);
          }
        }
        currentUserCache = null;
      } else if (session) {
        console.log("getCurrentUserInternal: Session found, verifying user actively with backend...");
        try {
          // Verify with backend that the user actually exists and is valid
          const { data: { user: authUser }, error: userError } = await supabase.auth.getUser();
          if (userError || !authUser) {
            const isNetworkError = userError?.message?.toLowerCase().includes('failed to fetch') || 
                                   userError?.message?.toLowerCase().includes('network') ||
                                   userError?.message?.toLowerCase().includes('timeout');
            
            if (isNetworkError) {
              console.warn("getCurrentUserInternal: Network error verifying user, relying on session", userError);
              currentUserCache = session.user;
            } else {
              console.warn("getCurrentUserInternal: Session is active, but user is deleted or session invalid. Logging out...", userError);
              try {
                await supabase.auth.signOut();
                localStorage.removeItem("literally-auth-token");
              } catch (signOutErr) {
                console.warn("Failed to sign out during user validation", signOutErr);
              }
              currentUserCache = null;
            }
          } else {
            console.log("getCurrentUserInternal: Backend user verified successfully");
            currentUserCache = authUser;
          }
        } catch (verifErr) {
          console.warn("getCurrentUserInternal: User backend verification failed, relying on session", verifErr);
          currentUserCache = session.user;
        }
      } else {
        currentUserCache = null;
      }
      return currentUserCache;
    } catch (err: any) {
      console.warn(
        `Supabase auth check: ${err.message}. Operating in logged-out state.`,
      );
      currentUserCache = null;
      return null;
    } finally {
      currentUserPromise = null;
    }
  })();

  return currentUserPromise;
};

export interface SearchFilters {
  sortBy: "relevance" | "popular" | "newest" | "oldest";
  genre: string;
  theme: string;
}

const generateLitIsbn = () => {
  const group = "1";
  const publisher = Math.floor(1000 + Math.random() * 9000).toString();
  const titleNum = Math.floor(1000 + Math.random() * 9000).toString();
  const base = `978${group}${publisher}${titleNum}`; // 12 digits
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(base[i], 10) * (i % 2 === 0 ? 1 : 3);
  }
  const checkDigit = (10 - (sum % 10)) % 10;
  return `978-${group}-${publisher}-${titleNum}-${checkDigit}`;
};

// --- RESTRUCTURED DYNAMIC PREMIUM MOD DATA ---
const MOCK_POSTS: Post[] = [];

const MOCK_BOOKS: Book[] = [];

const MOCK_COMPETITIONS: Competition[] = [];

const formatAuthorName = (
  fullName?: string,
  displayName?: string,
  fallback?: string,
) => {
  const name = displayName || fullName || fallback || "Anonymous Author";
  return cleanAuthorName(name);
};

const mapPostWithProfile = (post: any): Post => {
  if (!post) return post;
  const rawAuthorName = post.profiles
    ? formatAuthorName(
        post.profiles.full_name,
        post.profiles.display_name,
        post.author_name,
      )
    : post.author_name;
  return {
    ...post,
    author_avatar: post.profiles?.avatar_url || post.author_avatar,
    author_name: cleanAuthorName(rawAuthorName),
    media_url: post.media_url || ((post.type === 'article' || post.type === 'essay') ? post.background_url : undefined),
  };
};

async function fetchServerPosts(params: { type?: string; user_id?: string; search?: string; limit?: number; offset?: number } = {}): Promise<Post[]> {
  try {
    const query = new URLSearchParams();
    if (params.type) query.set('type', params.type);
    if (params.user_id) query.set('user_id', params.user_id);
    if (params.search) query.set('search', params.search);
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.offset) query.set('offset', params.offset.toString());

    const res = await fetch(`/api/posts?${query.toString()}`);
    if (res.ok) {
      const data = await res.json();
      return (data.posts || []).map(mapPostWithProfile);
    }
  } catch (e) {
    console.warn("fetchServerPosts error:", e);
  }
  return [];
}

async function saveServerPost(post: Post): Promise<Post | null> {
  try {
    const res = await fetch('/api/posts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(post)
    });
    if (res.ok) {
      const saved = await res.json();
      return mapPostWithProfile(saved);
    }
  } catch (e) {
    console.warn("saveServerPost error:", e);
  }
  return null;
}

const STABLE_PROFILE_COLUMNS =
  "id, email, full_name, display_name, bio, avatar_url, is_active, is_admin, date_of_birth, theme_preference, followers_count, following_count, total_views, book_reads, poem_views, total_likes, monthly_read_time, reading_streak, longest_streak, last_read_at, wallet_balance, ink_drop_balance, hide_wallet_balance, show_nsfw, is_adult, is_verified, bank_details, bank_details_snapshot, last_payout_date";
const MINIMAL_PROFILE_COLUMNS =
  "id, email, display_name, full_name, avatar_url";

let isPenNameColumnMissing = false;

const withTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number,
  errorMessage: string,
): Promise<T> => {
  let timeoutId: any;
  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(errorMessage));
    }, timeoutMs);
  });

  return Promise.race([
    promise.then(
      (val) => {
        clearTimeout(timeoutId);
        return val;
      },
      (err) => {
        clearTimeout(timeoutId);
        throw err;
      }
    ),
    timeoutPromise,
  ]);
};

const compressBlobToTinyBase64ByCanvas = (blob: Blob, maxDimension: number): Promise<string> => {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      resolve("");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/jpeg", 0.6)); // 60% quality is highly compressed and tiny
        } else {
          resolve("");
        }
      };
      img.onerror = () => resolve("");
      img.src = e.target?.result as string;
    };
    reader.onerror = () => resolve("");
    reader.readAsDataURL(blob);
  });
};

// --- VIEW DEDUPLICATION UTILITIES ---
// Prevents repeat view counting across page refreshes, tab revisits, and multi-clicks
const _pendingViewIncrements = new Set<string>();
const _sessionViewedPosts = new Set<string>();
const _sessionViewedProfiles = new Set<string>();

function getPersistentViewerId(): string {
  if (typeof window === "undefined") return "server";
  try {
    let viewerId = localStorage.getItem("literally_viewer_id");
    if (!viewerId) {
      viewerId = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `viewer_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("literally_viewer_id", viewerId);
    }
    return viewerId;
  } catch {
    return "anonymous";
  }
}

function isPostViewedLocally(postId: string): boolean {
  if (typeof window === "undefined" || !postId) return false;
  const normalizedId = postId.toLowerCase().trim();
  if (_sessionViewedPosts.has(normalizedId)) return true;
  try {
    const raw = localStorage.getItem("literally_viewed_posts");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.some((id: string) => String(id).toLowerCase().trim() === normalizedId)) {
        _sessionViewedPosts.add(normalizedId);
        return true;
      }
    }
    const legacy = localStorage.getItem("viewed_posts");
    if (legacy) {
      const parsedLegacy = JSON.parse(legacy);
      if (Array.isArray(parsedLegacy) && parsedLegacy.some((id: string) => String(id).toLowerCase().trim() === normalizedId)) {
        _sessionViewedPosts.add(normalizedId);
        return true;
      }
    }
  } catch {
    // Ignore JSON parse error
  }
  return false;
}

function markPostViewedLocally(postId: string): void {
  if (typeof window === "undefined" || !postId) return;
  const normalizedId = postId.toLowerCase().trim();
  _sessionViewedPosts.add(normalizedId);
  try {
    const raw = localStorage.getItem("literally_viewed_posts");
    let list: string[] = [];
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) list = parsed;
      } catch {}
    }
    if (!list.some(id => String(id).toLowerCase().trim() === normalizedId)) {
      list.push(normalizedId);
      if (list.length > 2000) list = list.slice(list.length - 2000);
      localStorage.setItem("literally_viewed_posts", JSON.stringify(list));
    }

    const legacyRaw = localStorage.getItem("viewed_posts");
    let legacyList: string[] = [];
    if (legacyRaw) {
      try {
        const parsedLegacy = JSON.parse(legacyRaw);
        if (Array.isArray(parsedLegacy)) legacyList = parsedLegacy;
      } catch {}
    }
    if (!legacyList.some(id => String(id).toLowerCase().trim() === normalizedId)) {
      legacyList.push(normalizedId);
      if (legacyList.length > 2000) legacyList = legacyList.slice(legacyList.length - 2000);
      localStorage.setItem("viewed_posts", JSON.stringify(legacyList));
    }
  } catch {
    // Ignore storage quota errors
  }
}

function isAuthorViewedLocally(authorId: string): boolean {
  if (typeof window === "undefined" || !authorId) return false;
  const normalizedId = authorId.toLowerCase().trim();
  if (_sessionViewedProfiles.has(normalizedId)) return true;
  try {
    const raw = localStorage.getItem("literally_viewed_authors");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.some((id: string) => String(id).toLowerCase().trim() === normalizedId)) {
        _sessionViewedProfiles.add(normalizedId);
        return true;
      }
    }
  } catch {}
  return false;
}

function markAuthorViewedLocally(authorId: string): void {
  if (typeof window === "undefined" || !authorId) return;
  const normalizedId = authorId.toLowerCase().trim();
  _sessionViewedProfiles.add(normalizedId);
  try {
    const raw = localStorage.getItem("literally_viewed_authors");
    let list: string[] = [];
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) list = parsed;
      } catch {}
    }
    if (!list.some(id => String(id).toLowerCase().trim() === normalizedId)) {
      list.push(normalizedId);
      if (list.length > 1000) list = list.slice(list.length - 1000);
      localStorage.setItem("literally_viewed_authors", JSON.stringify(list));
    }
  } catch {}
}

export const supabaseService = {
  async getCurrentUser() {
    return getCurrentUserInternal();
  },

  getFeedFromCache(key: string): any[] | null {
    const userSessionStr = localStorage.getItem('literally-auth-token');
    const userId = userSessionStr ? JSON.parse(userSessionStr).user?.id : 'guest';
    const scopedKey = `${key}_${userId}`;
    
    if (serviceCache[scopedKey]) {
      return serviceCache[scopedKey];
    }
    try {
      const stored = localStorage.getItem(`cache_${scopedKey}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        serviceCache[scopedKey] = parsed;
        return parsed;
      }
    } catch (e) {
      console.warn("Error reading from localStorage cache", e);
    }
    return null;
  },

  setFeedInCache(key: string, data: any[]) {
    const userSessionStr = localStorage.getItem('literally-auth-token');
    const userId = userSessionStr ? JSON.parse(userSessionStr).user?.id : 'guest';
    const scopedKey = `${key}_${userId}`;

    serviceCache[scopedKey] = data;
    try {
      localStorage.setItem(`cache_${scopedKey}`, JSON.stringify(data));
    } catch (e) {
      console.warn("Error writing to localStorage cache", e);
    }
  },

  isSupabaseMisconfigured() {
    if (!supabaseUrl) return false;
    const isRunAppSelf =
      supabaseUrl.includes(".run.app") && !supabaseUrl.includes("supabase.co");

    // Check if the URL points to the same hostname as the current app
    const currentHostname = window.location.hostname;
    const supabaseHostname = supabaseUrl
      .replace(/^https?:\/\//, "")
      .split("/")[0]
      .split(":")[0];
    const isSameOrigin = currentHostname === supabaseHostname;

    return isRunAppSelf || isSameOrigin;
  },

  async getProfile(userId?: string): Promise<Profile | null> {
    if (supabase) {
      try {
        let id = userId;
        const user = await getCurrentUserInternal();

        if (!id || id === "me") {
          if (!user) {
            console.log("getProfile: No user found, returning null");
            return null;
          }
          id = user.id;
        }

        const profileId = id as string;

        // Persistent Cache Check (Instant Return)
        if (resolvedProfileCache[profileId]) {
          console.log("getProfile: Cache HIT for ID:", profileId);
          return resolvedProfileCache[profileId];
        }

        // Concurrency guard for profile fetch
        if (profileFetchPromises[profileId]) {
          console.log(
            "getProfile: Returning existing promise for ID:",
            profileId,
          );
          return profileFetchPromises[profileId];
        }

        console.log("getProfile: Fetching profile for ID:", profileId);

        profileFetchPromises[profileId] = (async () => {
          let databaseContacted = false;
          try {
            // Sequential attempt 1: Primary fetch (select *)
            try {
              console.log("getProfile: Attempt 1 (Select *) starting...");
              const result = (await withTimeout(
                supabase
                  .from("profiles")
                  .select("*")
                  .eq("id", profileId)
                  .maybeSingle() as any,
                15000,
                "getProfile: Attempt 1 timeout",
              )) as any;
              databaseContacted = true;
              const { data, error } = result;
              if (data) {
                console.log("getProfile: Success on Attempt 1");
                const profile = this.processProfileResult(data);
                resolvedProfileCache[profileId] = profile;
                return profile;
              }
              if (error) {
                console.warn("getProfile: Attempt 1 error:", error.message);
              } else {
                // The query completed successfully but returned no results (data is null).
                // User exists in auth, but profile row hasn't propagated or doesn't exist yet.
                if (user && (user.id === profileId || profileId === "me")) {
                  console.log(
                    "getProfile: Profile row missing, immediately auto-creating...",
                  );
                  return await this.autoCreateProfile(user);
                }
                return null;
              }
            } catch (err: any) {
              console.warn("getProfile: Attempt 1 failed:", err.message);
            }

            // Sequential attempt 2: Stable columns fetch
            try {
              console.log("getProfile: Attempt 2 (Stable Columns) starting...");
              const result = (await withTimeout(
                supabase
                  .from("profiles")
                  .select(STABLE_PROFILE_COLUMNS)
                  .eq("id", profileId)
                  .maybeSingle() as any,
                10000,
                "getProfile: Attempt 2 timeout",
              )) as any;
              databaseContacted = true;
              const { data, error } = result;
              if (data) {
                console.log("getProfile: Success on Attempt 2");
                const profile = this.processProfileResult(data);
                resolvedProfileCache[profileId] = profile;
                return profile;
              }
              if (error) {
                console.warn("getProfile: Attempt 2 error:", error.message);
                if (
                  error.message.includes("column") ||
                  error.message.includes("has_selected_pen_name")
                ) {
                  isPenNameColumnMissing = true;
                }
              }
            } catch (err: any) {
              console.warn("getProfile: Attempt 2 failed or timed out:", err.message);
            }

            // Sequential attempt 3: Minimal columns fetch
            try {
              console.log(
                "getProfile: Attempt 3 (Minimal Columns) starting...",
              );
              const result = (await withTimeout(
                supabase
                  .from("profiles")
                  .select(MINIMAL_PROFILE_COLUMNS)
                  .eq("id", profileId)
                  .maybeSingle() as any,
                10000,
                "getProfile: Attempt 3 timeout",
              )) as any;
              databaseContacted = true;
              const { data, error } = result;
              if (data) {
                console.log("getProfile: Success on Attempt 3");
                const profile = this.processProfileResult(data);
                resolvedProfileCache[profileId] = profile;
                return profile;
              }
              if (error) {
                if (error.message.includes("permission denied")) isRlsMisconfigured = true;
                console.warn("getProfile: Attempt 3 error:", error.message);
              }
            } catch (err: any) {
              console.warn("getProfile: Attempt 3 failed:", err.message);
            }

            // Auto-create fallback if it's the current user and we still have nothing
            if (user && (user.id === profileId || profileId === "me")) {
              try {
                const spawned = await this.autoCreateProfile(user);
                if (spawned) return spawned;
              } catch (e) {
                console.error(
                  "autoCreateProfile failed, falling back to instant profile",
                  e,
                );
              }
            }

            if (user && (user.id === profileId || profileId === "me")) {
              return await this.autoCreateProfile(user);
            }

            console.log("getProfile: Profile completely missing from database. Returning null.");
            return null;
          } catch(e) {
            console.warn("getProfile: Unhandled network/fetch error, using fallback.", e);
            if (user && (user.id === profileId || profileId === "me")) {
              return await this.autoCreateProfile(user);
            }
            return null;
          } finally {
            // Cleanup promise from cache
            delete profileFetchPromises[profileId];
          }
        })();

        return profileFetchPromises[profileId];
      } catch (err) {
        console.error("getProfile error:", err);
        return null;
      }
    }
    return null;
  },

  async getProfileBySlugOrId(slugOrId?: string): Promise<Profile | null> {
    if (!slugOrId) return null;
    const clean = slugOrId.trim();
    const match = clean.match(UUID_PATTERN);
    if (match) {
      return await this.getProfile(match[0]);
    }

    if (supabase) {
      try {
        const cleanName = clean.replace(/-/g, ' ').trim();
        // First try matching display_name exactly (case-insensitive)
        let result = await supabase
          .from("profiles")
          .select(STABLE_PROFILE_COLUMNS)
          .ilike("display_name", clean)
          .limit(1)
          .maybeSingle();

        if (result.data) {
          return this.processProfileResult(result.data);
        }

        // Second try: replace hyphens with spaces
        result = await supabase
          .from("profiles")
          .select(STABLE_PROFILE_COLUMNS)
          .ilike("display_name", cleanName)
          .limit(1)
          .maybeSingle();

        if (result.data) {
          return this.processProfileResult(result.data);
        }

        // Third try: check full_name
        result = await supabase
          .from("profiles")
          .select(STABLE_PROFILE_COLUMNS)
          .ilike("full_name", cleanName)
          .limit(1)
          .maybeSingle();

        if (result.data) {
          return this.processProfileResult(result.data);
        }
      } catch (err) {
        console.warn("getProfileBySlugOrId lookup error:", err);
      }
    }
    return null;
  },

  async getProfileWithRetry(
    userId?: string,
    retries = 3,
    delayMs = 1000,
    backoffFactor = 2,
  ): Promise<Profile | null> {
    let attempt = 0;
    let currentDelay = delayMs;

    while (attempt < retries) {
      attempt++;
      try {
        const profile = await this.getProfile(userId);
        if (profile) {
          return profile;
        }
        console.log(
          `getProfileWithRetry: Attempt ${attempt}/${retries} returned null. Retrying in ${currentDelay}ms...`,
        );
      } catch (err) {
        console.warn(
          `getProfileWithRetry: Attempt ${attempt}/${retries} failed with error:`,
          err,
        );
      }

      if (attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, currentDelay));
        currentDelay *= backoffFactor;
      }
    }

    return await this.getProfile(userId);
  },

  async getTotalUsers(): Promise<number> {
    try {
      const { count, error } = await supabase
        .from("profiles")
        .select('*', { count: 'exact', head: true });
        
      if (error) {
        console.warn("getTotalUsers error:", error.message);
        return 10000; // Fallback
      }
      return count || 10000;
    } catch (e) {
      console.warn("getTotalUsers exception:", e);
      return 10000;
    }
  },

  // Helper to process profile results consistently
  processProfileResult(data: any): Profile {
    const result = data as any as Profile;

    if (result && result.id) {
      try {
        const isDismissed =
          localStorage.getItem(`literally_pen_name_dismissed_${result.id}`) === "true" ||
          localStorage.getItem(`literally_pen_name_claimed_${result.id}`) === "true";
        const savedDisplayName = localStorage.getItem(`literally_profile_display_name_${result.id}`);

        if (savedDisplayName && (!result.display_name || result.display_name.startsWith("User_"))) {
          result.display_name = savedDisplayName;
        }

        if (isDismissed) {
          result.has_selected_pen_name = true;
        }
      } catch (e) {
        console.warn("processProfileResult local storage check warning:", e);
      }
    }

    const isDefaultName = result.display_name
      ? /^User_\d+$/.test(result.display_name) || result.display_name.startsWith("User_")
      : true;

    if (
      !("has_selected_pen_name" in result) ||
      (result.has_selected_pen_name === false && !isDefaultName)
    ) {
      result.has_selected_pen_name = !isDefaultName;
    }

    // Check admin status
    if (result.email === "siwonikuayomide123@gmail.com") {
      result.is_admin = true;
    }

    // Resolve author name display preference (pen_name vs actual name)
    try {
      const savedPref = localStorage.getItem(`literally_name_display_preference_${result.id}`);
      if (savedPref === 'full_name' || savedPref === 'display_name') {
        result.name_display_preference = savedPref;
      } else if ((result as any).preferences?.name_display) {
        result.name_display_preference = (result as any).preferences.name_display;
      } else if (!result.name_display_preference) {
        result.name_display_preference = 'display_name';
      }
    } catch {
      if (!result.name_display_preference) {
        result.name_display_preference = 'display_name';
      }
    }

    // Cache the processed profile
    if (result && result.id) {
      resolvedProfileCache[result.id] = result;
    }

    return result;
  },

  async autoCreateProfile(user: any): Promise<Profile | null> {
    if (!user || !user.id) return null;

    if (resolvedProfileCache[user.id]) {
      return resolvedProfileCache[user.id];
    }

    console.log("getProfile: Auto-creating profile for current user:", user.id);
    const baseName = `User_${user.id.substring(0, 8)}`;
    const displayName = user.user_metadata?.display_name || user.user_metadata?.name || baseName;
    const fullName = user.user_metadata?.full_name || user.user_metadata?.name || displayName;

    const fallbackProfile: Profile = {
      id: user.id,
      email: user.email || "",
      full_name: fullName,
      display_name: displayName,
      bio: user.user_metadata?.bio || "Passionate reader & writer.",
      avatar_url: user.user_metadata?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.id}`,
      joined_date: new Date().toISOString(),
      created_at: new Date().toISOString(),
      has_selected_pen_name: false,
      wallet_balance: 0,
      ink_drop_balance: 0,
      total_likes: 0,
      followers_count: 0,
      following_count: 0,
      reading_streak: 0,
      is_admin: user.email === "siwonikuayomide123@gmail.com",
      is_active: true,
      show_nsfw: false,
      is_adult: false,
      is_verified: false,
    } as unknown as Profile;

    if (!supabase) {
      resolvedProfileCache[user.id] = fallbackProfile;
      return fallbackProfile;
    }

    const insertData: any = {
      id: user.id,
      display_name: displayName,
      full_name: fullName,
      date_of_birth: user.user_metadata?.date_of_birth || null,
      email: user.email,
      avatar_url: user.user_metadata?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.id}`,
    };

    try {
      // 1. Check if record already exists in database
      const { data: existing } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();

      if (existing) {
        console.log("autoCreateProfile: Profile row already exists in DB");
        const profile = this.processProfileResult(existing);
        resolvedProfileCache[user.id] = profile;
        return profile;
      }

      // 2. Try inserting record
      const result = (await supabase
        .from("profiles")
        .insert(insertData)
        .select()
        .maybeSingle()) as any;

      if (result?.data) {
        console.log("autoCreateProfile: Successfully inserted profile row");
        const profile = this.processProfileResult(result.data);
        resolvedProfileCache[user.id] = profile;
        return profile;
      }

      // 3. Re-fetch in case insert succeeded without select or trigger created it
      const { data: reFetched } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();

      if (reFetched) {
        const profile = this.processProfileResult(reFetched);
        resolvedProfileCache[user.id] = profile;
        return profile;
      }
    } catch (e: any) {
      console.warn("Auto-create profile attempt issue:", e?.message || e);
      if (e?.message?.includes("permission denied")) isRlsMisconfigured = true;

      try {
        const { data: retryFetch } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .maybeSingle();

        if (retryFetch) {
          const profile = this.processProfileResult(retryFetch);
          resolvedProfileCache[user.id] = profile;
          return profile;
        }
      } catch (retryErr) {
        console.warn("autoCreateProfile retry fetch warning:", retryErr);
      }
    }

    // 4. Guaranteed fallback profile for current user so the app loads smoothly
    console.log("autoCreateProfile: Using constructed fallback profile");
    resolvedProfileCache[user.id] = fallbackProfile;
    return fallbackProfile;
  },

  async updateProfile(updates: Partial<Profile>): Promise<Profile> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (user) {
        if (user.id) {
          try {
            if (updates.display_name && !updates.display_name.startsWith("User_")) {
              localStorage.setItem(`literally_profile_display_name_${user.id}`, updates.display_name);
              localStorage.setItem(`literally_pen_name_claimed_${user.id}`, "true");
              localStorage.setItem(`literally_pen_name_dismissed_${user.id}`, "true");
            }
            if (updates.has_selected_pen_name) {
              localStorage.setItem(`literally_pen_name_dismissed_${user.id}`, "true");
            }
            if (updates.name_display_preference) {
              localStorage.setItem(`literally_name_display_preference_${user.id}`, updates.name_display_preference);
            }
          } catch (e) {
            console.warn("updateProfile local storage sync error:", e);
          }
        }

        const cleanUpdates = { ...updates };
        if (isPenNameColumnMissing) {
          delete (cleanUpdates as any).has_selected_pen_name;
        }

        try {
          // Try with select('*') first for full data
          let data = null;
          let error = null;

          try {
            console.log(
              "updateProfile: Attempting primary update (select *)...",
            );
            const updatePromise = supabase
              .from("profiles")
              .update(cleanUpdates)
              .eq("id", user.id)
              .select("*")
              .maybeSingle();
            const result = (await withTimeout(
              updatePromise as any,
              30000,
              "updateProfile: Primary update timeout",
            )) as any;
            data = result.data;
            error = result.error;
          } catch (timeoutErr: any) {
            console.warn(
              "updateProfile: Initial update timed out or failed, trying stable columns fallback...",
              timeoutErr.message,
            );
            error = { message: timeoutErr.message || "Timeout" };
          }

          if (error || !data) {
            console.warn(
              "updateProfile: Error or empty data in primary update:",
              error?.message || "No data returned",
            );

            const isSchemaError =
              error?.message?.toLowerCase().includes("column") ||
              error?.message?.toLowerCase().includes("has_selected_pen_name") ||
              error?.message?.toLowerCase().includes("schema cache");

            if (isSchemaError) {
              isPenNameColumnMissing = true;
              delete (cleanUpdates as any).has_selected_pen_name;
            }

            console.log(
              `updateProfile: Attempting fallback update (stable columns) - isSchemaError: ${isSchemaError}`,
            );
            try {
              const stablePromise = supabase
                .from("profiles")
                .update(cleanUpdates)
                .eq("id", user.id)
                .select(STABLE_PROFILE_COLUMNS)
                .maybeSingle();
              const { data: stableData, error: stableError } = (await withTimeout(
                stablePromise as any,
                30000,
                "updateProfile: Stable columns update timeout",
              )) as any;

              if (!stableError && stableData) {
                data = stableData;
              }
            } catch (fallbackErr: any) {
              console.warn("updateProfile: Fallback update attempt warning:", fallbackErr?.message || fallbackErr);
            }
          }

          if (data) {
            const result = data as any as Profile;
            if (updates.has_selected_pen_name !== undefined) {
              result.has_selected_pen_name = updates.has_selected_pen_name;
            }
            if (user.email === "siwonikuayomide123@gmail.com") {
              result.is_admin = true;
            }

            // Sync author details to posts/books/comments asynchronously
            try {
              if (updates.avatar_url || updates.display_name || updates.full_name) {
                const postUpdates: any = {};
                const bookUpdates: any = {};
                const commentUpdates: any = {};
                if (updates.avatar_url) {
                  postUpdates.author_avatar = updates.avatar_url;
                  commentUpdates.user_avatar = updates.avatar_url;
                }
                if (updates.display_name || updates.full_name) {
                  const fullName = updates.full_name !== undefined ? updates.full_name : result.full_name;
                  const displayName = updates.display_name !== undefined ? updates.display_name : result.display_name;
                  const authorName = formatAuthorName(fullName, displayName);
                  postUpdates.author_name = authorName;
                  bookUpdates.author_name = authorName;
                  commentUpdates.user_name = authorName;
                }

                if (Object.keys(postUpdates).length > 0) {
                  await supabase.from("posts").update(postUpdates).eq("user_id", user.id);
                }
                if (Object.keys(bookUpdates).length > 0) {
                  const { error: bkErr } = await supabase.from("books").update(bookUpdates).eq("author_id", user.id);
                  if (bkErr && bkErr.message.toLowerCase().includes("column") && bkErr.message.toLowerCase().includes("author_name")) {
                    const fallbackBk = { ...bookUpdates };
                    delete fallbackBk.author_name;
                    if (Object.keys(fallbackBk).length > 0) {
                      await supabase.from("books").update(fallbackBk).eq("author_id", user.id);
                    }
                  }
                }
                if (Object.keys(commentUpdates).length > 0) {
                  await supabase.from("comments").update(commentUpdates).eq("user_id", user.id);
                }
              }
            } catch (syncErr) {
              console.warn("updateProfile related table sync warning:", syncErr);
            }

            resolvedProfileCache[user.id] = result;
            window.dispatchEvent(
              new CustomEvent("profileUpdated", { detail: result }),
            );
            return result;
          }

          // If DB update yielded no data (e.g. row missing in DB or RLS restriction), try upserting
          try {
            console.log("updateProfile: Row may be missing, trying upsert...");
            const upsertData = { id: user.id, email: user.email, ...cleanUpdates };
            const { data: upsertedData } = await supabase
              .from("profiles")
              .upsert(upsertData)
              .select("*")
              .maybeSingle();

            if (upsertedData) {
              const result = upsertedData as any as Profile;
              resolvedProfileCache[user.id] = result;
              window.dispatchEvent(
                new CustomEvent("profileUpdated", { detail: result }),
              );
              return result;
            }
          } catch (upsertErr) {
            console.warn("updateProfile: Upsert attempt warning:", upsertErr);
          }

          // Fallback to updating in-memory & cached profile gracefully
          console.warn("updateProfile: Applying updates to cached profile fallback");
          const existingProfile = (resolvedProfileCache[user.id]) || (await this.getProfile(user.id)) || ({
            id: user.id,
            email: user.email || "",
            full_name: user.user_metadata?.full_name || "User",
            display_name: user.user_metadata?.display_name || "User",
            joined_date: new Date().toISOString(),
          } as unknown as Profile);

          const mergedProfile: Profile = {
            ...existingProfile,
            ...updates,
          };

          resolvedProfileCache[user.id] = mergedProfile;
          window.dispatchEvent(
            new CustomEvent("profileUpdated", { detail: mergedProfile }),
          );
          return mergedProfile;

        } catch (err: any) {
          console.warn("updateProfile handled error with local merge:", err?.message || err);
          const existingProfile = (resolvedProfileCache[user.id]) || ({
            id: user.id,
            email: user.email || "",
            full_name: user.user_metadata?.full_name || "User",
            display_name: user.user_metadata?.display_name || "User",
            joined_date: new Date().toISOString(),
          } as unknown as Profile);

          const mergedProfile: Profile = {
            ...existingProfile,
            ...updates,
          };

          resolvedProfileCache[user.id] = mergedProfile;
          window.dispatchEvent(
            new CustomEvent("profileUpdated", { detail: mergedProfile }),
          );
          return mergedProfile;
        }
      }
    }
    throw new Error("Not logged in");
  },

  async deletePost(postId: string): Promise<boolean> {
    const realId = getRealId(postId);
    try {
      const { data } = await supabase?.auth.getSession() || { data: null };
      const token = data?.session?.access_token;
      const ids = new Set([realId, postId].filter(Boolean));
      for (const id of ids) {
        const response = await fetch(`/api/posts/${id}`, {
          method: 'DELETE',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok && response.status !== 404) {
          console.warn(`deletePost API returned ${response.status} for ${id}.`);
        }
      }
    } catch (e) {
      console.warn("deletePost API request failed:", e);
    }
    if (supabase) {
      try {
        const { error } = await supabase.from("posts").delete().eq("id", realId);
        if (error) {
          console.warn("deletePost DB warning:", error.message);
        }
      } catch (err) {
        console.warn("deletePost DB exception:", err);
      }
    }
    if (typeof window !== "undefined") {
      try {
        for (let index = localStorage.length - 1; index >= 0; index -= 1) {
          const key = localStorage.key(index);
          if (!key?.startsWith("comp_entries_")) continue;

          const entries = JSON.parse(localStorage.getItem(key) || "[]");
          if (!Array.isArray(entries)) continue;
          const removedEntries = entries.filter(
            (entry: any) => entry?.post_id === postId || entry?.post_id === realId,
          );
          if (removedEntries.length === 0) continue;

          localStorage.setItem(
            key,
            JSON.stringify(entries.filter(
              (entry: any) => entry?.post_id !== postId && entry?.post_id !== realId,
            )),
          );
          removedEntries.forEach((entry: any) => {
            if (entry?.id) localStorage.removeItem(`comp_entry_stage_${entry.id}`);
          });
        }
      } catch (err) {
        console.warn("Could not remove deleted post from local competition entries:", err);
      }
    }
    this.removeLocalPost(postId);
    this.removeLocalPost(realId);
    return true;
  },

  async deleteBook(bookId: string): Promise<boolean> {
    const realId = getRealId(bookId);
    if (supabase) {
      try {
        const { error } = await supabase.from("books").delete().eq("id", realId);
        if (error) {
          console.warn("deleteBook DB warning:", error.message);
        }
      } catch (err) {
        console.warn("deleteBook DB exception:", err);
      }
    }
    this.removeLocalBook(bookId);
    this.removeLocalBook(realId);
    return true;
  },

  async deleteComment(commentId: string): Promise<boolean> {
    const realId = getRealId(commentId);
    if (supabase) {
      try {
        const { error } = await supabase.from("comments").delete().eq("id", realId);
        if (error) {
          console.warn("deleteComment DB warning:", error.message);
        }
      } catch (err) {
        console.warn("deleteComment DB exception:", err);
      }
    }
    this.removeLocalComment(commentId);
    this.removeLocalComment(realId);
    return true;
  },

  removeLocalPost(postId: string): void {
    try {
      const existing = this.getLocalPosts();
      const updated = existing.filter((p) => p.id !== postId);
      localStorage.setItem("literally_local_posts", JSON.stringify(updated));
    } catch (e) {
      console.warn("Failed to remove local post:", e);
    }
  },

  removeLocalBook(bookId: string): void {
    try {
      const existing = this.getLocalBooks();
      const updated = existing.filter((b) => b.id !== bookId);
      localStorage.setItem("literally_local_books", JSON.stringify(updated));
    } catch (e) {
      console.warn("Failed to remove local book:", e);
    }
  },

  removeLocalComment(commentId: string): void {
    try {
      const local: Comment[] = JSON.parse(localStorage.getItem("literally_local_comments") || "[]");
      const updated = local.filter((c: Comment) => c.id !== commentId);
      localStorage.setItem("literally_local_comments", JSON.stringify(updated));
    } catch (e) {
      console.warn("Failed to remove local comment:", e);
    }
  },

  // Local storage post/book helpers for instant zero-wait publishing & offline resilience
  getLocalPosts(): Post[] {
    try {
      const raw = localStorage.getItem("literally_local_posts");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  },

  saveLocalPost(post: Post): void {
    try {
      const existing = this.getLocalPosts();
      const updated = [post, ...existing.filter((p) => p.id !== post.id)];
      localStorage.setItem("literally_local_posts", JSON.stringify(updated));
    } catch (e) {
      console.warn("Failed to save local post:", e);
    }
  },

  getLocalBooks(): Book[] {
    try {
      const raw = localStorage.getItem("literally_local_books");
      const list: Book[] = raw ? JSON.parse(raw) : [];
      return list.map(b => this.normalizeBook(b));
    } catch {
      return [];
    }
  },

  normalizeBook(b: any): Book {
    if (!b) return b;
    let status: 'completed' | 'ongoing' = 'ongoing';
    if (b.status === 'completed' || b.status === 'ongoing') {
      status = b.status;
    } else if (Array.isArray(b.themes)) {
      if (b.themes.includes('status:completed') || b.themes.includes('completed')) {
        status = 'completed';
      } else if (b.themes.includes('status:ongoing') || b.themes.includes('ongoing')) {
        status = 'ongoing';
      }
    }
    return { ...b, status };
  },

  saveLocalBook(book: Book): void {
    try {
      const normalized = this.normalizeBook(book);
      const existing = this.getLocalBooks();
      const updated = [normalized, ...existing.filter((b) => b.id !== normalized.id)];
      localStorage.setItem("literally_local_books", JSON.stringify(updated));
    } catch (e) {
      console.warn("Failed to save local book:", e);
    }
  },

  getLocalChapters(bookId?: string): Chapter[] {
    try {
      const raw = localStorage.getItem("literally_local_chapters");
      const chapters: Chapter[] = raw ? JSON.parse(raw) : [];
      if (bookId) {
        return chapters.filter((c) => c.book_id === bookId);
      }
      return chapters;
    } catch {
      return [];
    }
  },

  saveLocalChapter(chapter: Chapter): void {
    try {
      const existing = this.getLocalChapters();
      const updated = [chapter, ...existing.filter((c) => c.id !== chapter.id)];
      localStorage.setItem("literally_local_chapters", JSON.stringify(updated));
    } catch (e) {
      console.warn("Failed to save local chapter:", e);
    }
  },

  async createPost(post: Post): Promise<Post> {
    const safePostToInsert: any = {
      user_id: post.user_id,
      title: post.title || "Untitled",
      content: post.content,
      background_url: post.background_url || post.media_url || null,
      media_url: post.media_url || ((post.type === 'article' || post.type === 'essay') ? post.background_url : undefined),
      status: post.status || "published",
      author_name: cleanAuthorName(post.author_name || "Author"),
      author_avatar: post.author_avatar || null,
      type: post.type || "poem",
      is_nsfw: post.is_nsfw || false,
      is_ai_generated: post.is_ai_generated || false,
      theme: post.theme || null,
      hashtag: post.hashtag || null,
      created_at: post.created_at || new Date().toISOString(),
      likes_count: post.likes_count || 0,
      comments_count: post.comments_count || 0,
      views_count: post.views_count || 0,
    };

    if (post.id && post.id.includes('-') && post.id.length === 36) {
      safePostToInsert.id = post.id;
    }

    let createdPost: Post | null = null;

    if (supabase) {
      try {
        const { data: profileExists } = await supabase
          .from("profiles")
          .select("id")
          .eq("id", post.user_id)
          .maybeSingle();
        if (!profileExists) {
          const user = await getCurrentUserInternal();
          if (user && user.id === post.user_id) {
            await this.autoCreateProfile(user);
          }
        }
      } catch (profileChkErr) {}

      try {
        const { data, error } = await supabase
          .from("posts")
          .insert(safePostToInsert)
          .select()
          .maybeSingle();

        if (!error && data) {
          createdPost = mapPostWithProfile(data);
        }
      } catch (err: any) {
        console.warn("createPost Supabase execution failed:", err);
      }
    }

    // Always sync with global server API for immediate cross-user availability across all browsers
    try {
      const serverResult = await saveServerPost(createdPost || safePostToInsert);
      if (serverResult) {
        createdPost = serverResult;
      }
    } catch (err) {
      console.warn("createPost server sync error:", err);
    }

    const finalPost: Post = createdPost || (safePostToInsert as Post);
    this.saveLocalPost(finalPost);
    return finalPost;
  },

  async getFeed(): Promise<Post[]> {
    let dbPosts: Post[] = [];
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from("posts")
          .select("*, profiles!fk_posts_profiles(avatar_url, display_name)")
          .order("created_at", { ascending: false });
        if (!error && data) {
          dbPosts = data.map(mapPostWithProfile);
        } else {
          const { data: plainData } = await supabase
            .from("posts")
            .select("*")
            .order("created_at", { ascending: false });
          if (plainData) dbPosts = plainData.map(mapPostWithProfile);
        }
      } catch (err) {
        console.warn("getFeed error:", err);
      }
    }
    const serverPosts = await fetchServerPosts();
    const localPosts = this.getLocalPosts();
    const combined = [...localPosts, ...serverPosts, ...dbPosts];
    const deduplicated = Array.from(new Map(combined.map((p) => [p.id, p])).values());
    return deduplicated.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
  },

  async getPostsByIds(ids: string[]): Promise<Post[]> {
    if (ids.length === 0) return [];
    if (supabase) {
      const { data, error } = await supabase
        .from("posts")
        .select("*, profiles!fk_posts_profiles(avatar_url, display_name)")
        .in("id", ids);
      if (!error && data) {
        return data.map(mapPostWithProfile);
      }
      const { data: plain } = await supabase
        .from("posts")
        .select("*")
        .in("id", ids);
      return (plain || []).map(mapPostWithProfile);
    }
    return [];
  },

  async getBooksByIds(ids: string[]): Promise<Book[]> {
    if (ids.length === 0) return [];
    if (supabase) {
      const { data } = await supabase.from("books").select("*").in("id", ids);
      return data || [];
    }
    return [];
  },

  async getChaptersByIds(ids: string[]): Promise<Chapter[]> {
    if (ids.length === 0) return [];
    if (supabase) {
      const { data } = await supabase
        .from("chapters")
        .select("*")
        .in("id", ids);
      return data || [];
    }
    return [];
  },

  async getRelatedPosts(
    postId: string,
    authorId: string,
    type: string = "article",
  ): Promise<Post[]> {
    if (supabase) {
      // Fetch posts by the same author or with similar type, excluding the current post
      const { data, error } = await supabase
        .from("posts")
        .select("*, profiles!fk_posts_profiles(avatar_url, display_name)")
        .eq("type", type)
        .neq("id", postId)
        .limit(5);

      if (!error && data) return data.map(mapPostWithProfile);
      const { data: plain } = await supabase
        .from("posts")
        .select("*")
        .eq("type", type)
        .neq("id", postId)
        .limit(5);
      return (plain || []).map(mapPostWithProfile);
    }
    return [];
  },

  async getRelatedByTopic(
    postId: string,
    type: string = "article",
    theme?: string,
  ): Promise<Post[]> {
    const realId = getRealId(postId);
    if (supabase) {
      let query = supabase
        .from("posts")
        .select("*, profiles!fk_posts_profiles(avatar_url, display_name)")
        .eq("type", type)
        .neq("id", realId);

      if (theme) {
        query = query.eq("theme", theme);
      }

      const { data, error } = await query.limit(4);
      if (!error && data) return data.map(mapPostWithProfile);

      let fallbackQuery = supabase
        .from("posts")
        .select("*")
        .eq("type", type)
        .neq("id", realId);
      if (theme) fallbackQuery = fallbackQuery.eq("theme", theme);
      const { data: plain } = await fallbackQuery.limit(4);
      return (plain || []).map(mapPostWithProfile);
    }
    return [];
  },

  async getOtherPostsByAuthor(
    postId: string,
    authorId: string,
  ): Promise<Post[]> {
    const realId = getRealId(postId);
    if (supabase) {
      const { data, error } = await supabase
        .from("posts")
        .select("*, profiles!fk_posts_profiles(avatar_url, display_name)")
        .eq("user_id", authorId)
        .neq("id", realId)
        .limit(4);

      if (!error && data) return data.map(mapPostWithProfile);
      const { data: plain } = await supabase
        .from("posts")
        .select("*")
        .eq("user_id", authorId)
        .neq("id", realId)
        .limit(4);
      return (plain || []).map(mapPostWithProfile);
    }
    return [];
  },

  async checkMonetizationEligibility(
    type: "poem" | "article" | "book",
    id: string,
    authorId: string,
  ): Promise<any> {
    if (supabase) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("followers_count, monthly_read_time")
        .eq("id", authorId)
        .maybeSingle();
      if (!profile) return { isEligible: false, reason: "Profile not found" };

      const followers = profile.followers_count || 0;
      const readTime = profile.monthly_read_time || 0;
      const hasFollowers = followers >= 500;
      const hasReadTime = readTime >= 3000; // 50 hours within 60 days
      const isEligible = hasFollowers && hasReadTime;

      return {
        isEligible,
        reason: isEligible
          ? "Eligible"
          : !hasFollowers
            ? "Minimum 500 followers required to reach monetization stage"
            : "Minimum 3,000 minutes (50 hrs) read time within 60 days required",
        followersCount: followers,
        readTime60d: readTime,
        missingFollowers: Math.max(0, 500 - followers),
        missingReadTime: Math.max(0, 3000 - readTime),
      };
    }
    return { isEligible: false, reason: "Supabase not configured" };
  },

  async trackReadTime(
    id: string,
    type: "post" | "book",
    durationMinutes: number,
  ) {
    if (supabase) {
      try {
        if (type === "post") {
          const { data: post } = await supabase
            .from("posts")
            .select("user_id, monthly_read_time")
            .eq("id", id)
            .maybeSingle();
          if (post) {
            await supabase
              .from("posts")
              .update({
                monthly_read_time:
                  (post.monthly_read_time || 0) + durationMinutes,
              })
              .eq("id", id);
            const { data: profile } = await supabase
              .from("profiles")
              .select("monthly_read_time")
              .eq("id", post.user_id)
              .maybeSingle();
            if (profile) {
              await supabase
                .from("profiles")
                .update({
                  monthly_read_time:
                    (profile.monthly_read_time || 0) + durationMinutes,
                })
                .eq("id", post.user_id);
            }
          }
        } else if (type === "book") {
          const { data: book } = await supabase
            .from("books")
            .select("monthly_read_time")
            .eq("id", id)
            .maybeSingle();
          if (book) {
            await supabase
              .from("books")
              .update({
                monthly_read_time:
                  (book.monthly_read_time || 0) + durationMinutes,
              })
              .eq("id", id);
          }
        }
      } catch (err) {
        console.warn("Failed to track read time (likely missing column):", err);
      }
    }
  },

  saveLocalComment(comment: Comment) {
    try {
      const local = JSON.parse(localStorage.getItem("literally_local_comments") || "[]");
      const exists = local.some((c: Comment) => c.id === comment.id);
      if (!exists) {
        local.unshift(comment);
        localStorage.setItem("literally_local_comments", JSON.stringify(local.slice(0, 500)));
      }
    } catch (e) {
      console.warn("Failed to save local comment", e);
    }
  },

  getLocalComments(resourceId: string): Comment[] {
    try {
      const realId = getRealId(resourceId);
      const local: Comment[] = JSON.parse(localStorage.getItem("literally_local_comments") || "[]");
      return local.filter((c: any) => c.post_id === realId || c.book_id === realId || c.chapter_id === realId || c.post_id === resourceId || c.book_id === resourceId);
    } catch (e) {
      return [];
    }
  },

  async getComments(resourceId: string): Promise<Comment[]> {
    const realId = getRealId(resourceId);
    const targetUuid = UUID_PATTERN.test(realId) ? realId : (UUID_PATTERN.test(resourceId) ? resourceId : null);
    let dbComments: Comment[] = [];
    if (supabase && targetUuid) {
      try {
        const { data } = await supabase
          .from("comments")
          .select("*")
          .or(
            `post_id.eq.${targetUuid},book_id.eq.${targetUuid},chapter_id.eq.${targetUuid}`,
          )
          .order("created_at", { ascending: false });
        dbComments = (data || []).map((c: any) => ({
          ...c,
          user_name: cleanAuthorName(c.user_name)
        }));
      } catch (e) {
        console.warn("getComments DB error:", e);
      }
    }

    let serverComments: Comment[] = [];
    try {
      const res = await fetch(`/api/posts/${realId}/comments`);
      if (res.ok) {
        const d = await res.json();
        serverComments = (d.comments || []).map((c: any) => ({
          ...c,
          user_name: cleanAuthorName(c.user_name)
        }));
      }
    } catch (e) {}

    const localComments = this.getLocalComments(resourceId).map((c: any) => ({
      ...c,
      user_name: cleanAuthorName(c.user_name)
    }));
    const combined = [...localComments, ...serverComments, ...dbComments];
    const deduplicated = Array.from(new Map(combined.map((c) => [c.id, c])).values());
    return deduplicated.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  },

  async createComment(payload: any): Promise<Comment> {
    const cleanPayload = { ...payload };
    if (cleanPayload.id && !(cleanPayload.id.includes('-') && cleanPayload.id.length === 36)) {
        delete cleanPayload.id;
    }
    
    // Resolve user ID
    if (!cleanPayload.user_id || cleanPayload.user_id === "me") {
      const user = await getCurrentUserInternal();
      if (user) cleanPayload.user_id = user.id;
    }

    if (cleanPayload.post_id) cleanPayload.post_id = getRealId(cleanPayload.post_id);
    if (cleanPayload.book_id) cleanPayload.book_id = getRealId(cleanPayload.book_id);
    if (cleanPayload.chapter_id) cleanPayload.chapter_id = getRealId(cleanPayload.chapter_id);
    cleanPayload.user_name = cleanAuthorName(cleanPayload.user_name);

    let savedData: any = null;

    if (supabase) {
      try {
        const { data, error } = await supabase
          .from("comments")
          .insert(cleanPayload)
          .select()
          .maybeSingle();

        if (!error && data) {
          savedData = data;
        } else if (error) {
          console.warn("createComment primary insert failed:", error.message);
        }
      } catch (err) {
        console.warn("createComment exception:", err);
      }
    }

    const resultComment: Comment = savedData ? {
      ...savedData,
      user_name: cleanAuthorName(savedData.user_name)
    } : {
      id: `local_comment_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      created_at: new Date().toISOString(),
      ...cleanPayload,
      user_name: cleanAuthorName(cleanPayload.user_name)
    };

    // Store in local storage backup so comment never disappears
    this.saveLocalComment(resultComment);

    // Create notification asynchronously for the author
    if (cleanPayload.post_id || cleanPayload.book_id) {
      try {
        let authorId: string | null = null;
        let resType = "post";
        let resId = cleanPayload.post_id;

        if (cleanPayload.post_id) {
          const { data: post } = await supabase.from("posts").select("user_id").eq("id", cleanPayload.post_id).maybeSingle();
          authorId = post?.user_id;
        } else if (cleanPayload.book_id) {
          resType = "book";
          resId = cleanPayload.book_id;
          const { data: book } = await supabase.from("books").select("author_id").eq("id", cleanPayload.book_id).maybeSingle();
          authorId = book?.author_id;
        }

        if (authorId && authorId !== cleanPayload.user_id) {
          await supabase.from("notifications").insert({
            user_id: authorId,
            actor_id: cleanPayload.user_id,
            type: "comment",
            resource_id: resId,
            resource_type: resType,
            preview_text: cleanPayload.content,
            is_read: false,
          });
        }
      } catch (nErr) {
        console.warn("Failed to create comment notification:", nErr);
      }
    }

    return resultComment;
  },

  async toggleBookmark(
    id: string,
    type: "post" | "book" | "chapter",
  ): Promise<boolean> {
    const realId = getRealId(id);
    const key = `literally_saved_${type}s`;
    const savedIds = JSON.parse(localStorage.getItem(key) || "[]");
    
    const index = savedIds.indexOf(id);
    const realIndex = savedIds.indexOf(realId);
    
    let newState = false;
    if (index === -1 && realIndex === -1) {
      savedIds.push(realId);
      newState = true;
    } else {
      if (index !== -1) savedIds.splice(index, 1);
      const updatedRealIndex = savedIds.indexOf(realId);
      if (updatedRealIndex !== -1) savedIds.splice(updatedRealIndex, 1);
      newState = false;
    }
    localStorage.setItem(key, JSON.stringify(savedIds));
    return newState;
  },

  async isBookmarked(
    id: string,
    type: "post" | "book" | "chapter",
  ): Promise<boolean> {
    const realId = getRealId(id);
    const key = `literally_saved_${type}s`;
    const savedIds = JSON.parse(localStorage.getItem(key) || "[]");
    return savedIds.includes(id) || savedIds.includes(realId);
  },

  async toggleSavePost(id: string): Promise<boolean> {
    return this.toggleBookmark(id, "post");
  },

  async isPostSaved(id: string): Promise<boolean> {
    return this.isBookmarked(id, "post");
  },

  async toggleLike(
    id: string,
    userId: string,
    shouldLike: boolean,
    resourceType: "post" | "book" | "chapter" = "post",
  ) {
    const realId = getRealId(id);
    // 1. Local Storage fallback first
    const localKey = `literally_liked_${resourceType}s`;
    const likedIds = JSON.parse(localStorage.getItem(localKey) || "[]");
    const localIndex = likedIds.indexOf(id);
    const localRealIndex = likedIds.indexOf(realId);

    if (shouldLike) {
      if (localIndex === -1 && localRealIndex === -1) {
        likedIds.push(realId);
        localStorage.setItem(localKey, JSON.stringify(likedIds));
      }
    } else {
      if (localIndex !== -1) {
        likedIds.splice(localIndex, 1);
      }
      const updatedRealIndex = likedIds.indexOf(realId);
      if (updatedRealIndex !== -1) {
        likedIds.splice(updatedRealIndex, 1);
      }
      localStorage.setItem(localKey, JSON.stringify(likedIds));
    }

    // Update local storage cached posts / books likes_count so offline / local cache remains accurate
    if (resourceType === "post") {
      try {
        fetch(`/api/posts/${realId}/like`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, shouldLike })
        }).catch(() => {});
      } catch (e) {}
      try {
        const localPosts = this.getLocalPosts();
        let updated = false;
        for (const p of localPosts) {
          if (p.id === id || p.id === realId) {
            p.likes_count = shouldLike ? (p.likes_count || 0) + 1 : Math.max(0, (p.likes_count || 0) - 1);
            updated = true;
          }
        }
        if (updated) {
          localStorage.setItem("literally_local_posts", JSON.stringify(localPosts));
        }
      } catch (e) {
        console.warn("Failed updating local post likes count:", e);
      }
    } else if (resourceType === "book") {
      try {
        const localBooks = this.getLocalBooks();
        let updated = false;
        for (const b of localBooks) {
          if (b.id === id || b.id === realId) {
            b.likes_count = shouldLike ? (b.likes_count || 0) + 1 : Math.max(0, (b.likes_count || 0) - 1);
            updated = true;
          }
        }
        if (updated) {
          localStorage.setItem("literally_local_books", JSON.stringify(localBooks));
        }
      } catch (e) {
        console.warn("Failed updating local book likes count:", e);
      }
    }

    // 2. Database Sync
    if (supabase) {
      try {
        let realUserId = userId;
        if (userId === "me") {
          const user = await getCurrentUserInternal();
          if (!user) return;
          realUserId = user.id;
        }

        if (shouldLike) {
          const payload: any = { user_id: realUserId };
          if (resourceType === "post") payload.post_id = realId;
          else if (resourceType === "book") payload.book_id = realId;
          else if (resourceType === "chapter") payload.chapter_id = realId;

          const { error } = await supabase.from("likes").insert(payload);
          if (error && error.code !== "23505") {
            console.warn("toggleLike insert notice (local backup active):", error.message || error);
          }
          
          // Attempt to increment the actual counts (ignoring RLS errors if they occur)
          try {
             if (resourceType === "post") {
                 const { data: post } = await supabase.from("posts").select("user_id, title, likes_count").eq("id", realId).maybeSingle();
                 if (post) {
                     await supabase.from("posts").update({ likes_count: (post.likes_count || 0) + 1 }).eq("id", realId);
                     if (post.user_id) {
                         const { data: author } = await supabase.from("profiles").select("total_likes").eq("id", post.user_id).maybeSingle();
                         if (author) {
                             await supabase.from("profiles").update({ total_likes: (author.total_likes || 0) + 1 }).eq("id", post.user_id);
                         }
                         if (post.user_id !== realUserId) {
                             await supabase.from("notifications").insert({
                               user_id: post.user_id,
                               actor_id: realUserId,
                               type: "like",
                               resource_id: realId,
                               resource_type: "post",
                               preview_text: `liked your work "${post.title || 'Untitled'}"`,
                               is_read: false
                             });
                         }
                     }
                 }
             } else if (resourceType === "book") {
                 const { data: book } = await supabase.from("books").select("author_id, title").eq("id", realId).maybeSingle();
                 if (book && book.author_id && book.author_id !== realUserId) {
                     await supabase.from("notifications").insert({
                       user_id: book.author_id,
                       actor_id: realUserId,
                       type: "like",
                       resource_id: realId,
                       resource_type: "book",
                       preview_text: `liked your book "${book.title}"`,
                       is_read: false
                     });
                 }
             }
          } catch (e) {
             console.warn("Failed to increment like counts/notify on post/profile", e);
          }

        } else {
          const match: any = { user_id: realUserId };
          if (resourceType === "post") match.post_id = realId;
          else if (resourceType === "book") match.book_id = realId;
          else if (resourceType === "chapter") match.chapter_id = realId;

          const { error } = await supabase.from("likes").delete().match(match);
          if (error) {
            console.warn("toggleLike delete notice (local backup active):", error.message || error);
          }

          // Attempt to decrement the actual counts (ignoring RLS errors if they occur)
          try {
             if (resourceType === "post") {
                 const { data: post } = await supabase.from("posts").select("user_id, likes_count").eq("id", realId).maybeSingle();
                 if (post) {
                     await supabase.from("posts").update({ likes_count: Math.max(0, (post.likes_count || 0) - 1) }).eq("id", realId);
                     if (post.user_id) {
                         const { data: author } = await supabase.from("profiles").select("total_likes").eq("id", post.user_id).maybeSingle();
                         if (author) {
                             await supabase.from("profiles").update({ total_likes: Math.max(0, (author.total_likes || 0) - 1) }).eq("id", post.user_id);
                         }
                     }
                 }
             }
          } catch (e) {
             console.warn("Failed to decrement like counts on post/profile due to RLS", e);
          }
        }
      } catch (e) {
        console.warn("toggleLike DB sync issue, handled in local fallback:", e);
      }
    }
  },

  async toggleFollow(targetUserId: string, following: boolean) {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) return;

      if (following) {
        const { error } = await supabase
          .from("follows")
          .insert({ follower_id: user.id, following_id: targetUserId });
        if (error) throw error;

        // Notify target user
        try {
          await supabase.from("notifications").insert({
            user_id: targetUserId,
            actor_id: user.id,
            type: "follow",
            resource_id: user.id, // The resource is the follower themselves
            resource_type: "user",
            is_read: false,
          });
        } catch (err) {
          console.warn("Failed to create notification (likely RLS):", err);
        }
      } else {
        const { error } = await supabase
          .from("follows")
          .delete()
          .match({ follower_id: user.id, following_id: targetUserId });
        if (error) throw error;
      }
    }
  },

  async uploadImage(bucket: string, fileOrUrl: string | Blob): Promise<string> {
    if (!supabase) throw new Error("Supabase not configured");

    const user = await getCurrentUserInternal();
    if (!user) throw new Error("Not authenticated");

    let publicUrl = "";
    let blobToUpload: Blob | null = null;

    if (typeof fileOrUrl === "string" && fileOrUrl.startsWith("blob:")) {
      const response = await fetch(fileOrUrl);
      blobToUpload = await response.blob();
    } else if (typeof fileOrUrl !== "string") {
      blobToUpload = fileOrUrl;
    } else {
      publicUrl = fileOrUrl;
    }

    if (blobToUpload) {
      const fileName = `${user.id}/${Date.now()}.jpg`;
      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, blobToUpload, { upsert: true });

      if (uploadError) {
        console.warn(
          `Storage upload to ${bucket} failed, falling back to ultra-compact thumbnail`,
          uploadError,
        );
        try {
          publicUrl = await compressBlobToTinyBase64ByCanvas(blobToUpload, 400);
        } catch {
          publicUrl = "https://images.unsplash.com/photo-1474932430478-367db2683bfc?w=400";
        }
      } else {
        const {
          data: { publicUrl: url },
        } = supabase.storage.from(bucket).getPublicUrl(fileName);
        publicUrl = url;
      }
    }
    return publicUrl;
  },

  async updateAvatar(fileOrUrl: string | Blob): Promise<string> {
    if (!supabase) throw new Error("Supabase not configured");

    const user = await getCurrentUserInternal();
    if (!user) throw new Error("Not authenticated");

    let publicUrl = "";
    let blobToUpload: Blob | null = null;

    if (typeof fileOrUrl === "string" && fileOrUrl.startsWith("blob:")) {
      const response = await fetch(fileOrUrl);
      blobToUpload = await response.blob();
    } else if (typeof fileOrUrl !== "string") {
      blobToUpload = fileOrUrl;
    } else {
      publicUrl = fileOrUrl;
    }

    if (blobToUpload) {
      const fileName = `${user.id}/${Date.now()}.jpg`;
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(fileName, blobToUpload, { upsert: true });

      if (uploadError) {
        console.warn(
          "Storage upload failed, falling back to ultra-compact avatar thumbnail",
          uploadError,
        );
        try {
          publicUrl = await compressBlobToTinyBase64ByCanvas(blobToUpload, 120);
        } catch {
          publicUrl = `https://api.dicebear.com/7.x/pixel-art/svg?seed=${user.id}`;
        }
      } else {
        const {
          data: { publicUrl: url },
        } = supabase.storage.from("avatars").getPublicUrl(fileName);
        publicUrl = url;
      }
    }

    const { error } = await supabase
      .from("profiles")
      .update({ avatar_url: publicUrl })
      .eq("id", user.id);

    if (error) throw error;

    // Update all posts by this user
    await supabase
      .from("posts")
      .update({ author_avatar: publicUrl })
      .eq("user_id", user.id);

    return publicUrl;
  },

  async getLibraryBooks(): Promise<Book[]> {
    let dbBooks: Book[] = [];
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from("books")
          .select("*")
          .or("is_published.eq.true,is_published.is.null,status.eq.published")
          .order("created_at", { ascending: false });
        if (!error && data) {
          dbBooks = data;
        } else {
          const { data: fallbackData } = await supabase
            .from("books")
            .select("*")
            .order("created_at", { ascending: false });
          if (fallbackData) dbBooks = fallbackData;
        }
      } catch (e) {
        console.warn("getLibraryBooks error:", e);
      }
    }
    const localBooks = this.getLocalBooks();
    const combined = [...localBooks, ...dbBooks.map(b => this.normalizeBook(b))];
    return Array.from(new Map(combined.map((b) => [b.id, this.normalizeBook(b)])).values());
  },

  async getMyBooks(userId: string): Promise<Book[]> {
    let dbBooks: Book[] = [];
    if (supabase) {
      let targetId = userId;
      if (userId === "me") {
        const user = await getCurrentUserInternal();
        if (user) targetId = user.id;
      }
      try {
        const { data } = await supabase
          .from("books")
          .select("*")
          .eq("author_id", targetId)
          .order("created_at", { ascending: false });
        dbBooks = data || [];
        
        for (const b of dbBooks) {
            try {
                const { count } = await supabase.from('likes').select('*', { count: 'exact', head: true }).eq('book_id', b.id);
                if (count !== null && count > 0) b.likes_count = Math.max(b.likes_count || 0, count);
            } catch (e) {}
        }
      } catch (e) {
        console.warn("getMyBooks error:", e);
      }
    }
    const localBooks = this.getLocalBooks().filter(
      (b) => b.author_id === userId || userId === "me",
    );
    const combined = [...localBooks, ...dbBooks.map(b => this.normalizeBook(b))];
    return Array.from(new Map(combined.map((b) => [b.id, this.normalizeBook(b)])).values());
  },

  async getBook(id: string): Promise<Book | null> {
    const realId = getRealId(id);
    const targetUuid = UUID_PATTERN.test(realId) ? realId : (UUID_PATTERN.test(id) ? id : null);
    let dbBook: Book | null = null;
    if (supabase && targetUuid) {
      try {
        const { data } = await supabase
          .from("books")
          .select("*")
          .eq("id", targetUuid)
          .maybeSingle();
        dbBook = data;
        if (dbBook) {
          const bookUuid = dbBook.id;
          // Dynamically count likes from likes table
          const { count: realLikes } = await supabase
            .from("likes")
            .select("*", { count: "exact", head: true })
            .eq("book_id", bookUuid);
          if (realLikes !== null && realLikes !== undefined && realLikes > 0) {
            dbBook.likes_count = Math.max(dbBook.likes_count || 0, realLikes);
          }

          // Dynamically count comments from comments table
          const { count: realComments } = await supabase
            .from("comments")
            .select("*", { count: "exact", head: true })
            .eq("book_id", bookUuid);
          if (realComments !== null && realComments !== undefined && realComments > 0) {
            dbBook.comments_count = Math.max(dbBook.comments_count || 0, realComments);
          }
        }
      } catch (e) {
        console.warn("getBook DB error, using mocks:", e);
      }
    }
    if (dbBook) return this.normalizeBook(dbBook);

    const localBooks = this.getLocalBooks();
    const foundLocal = localBooks.find((b) => b.id === id || b.id === realId || (targetUuid && b.id === targetUuid));
    if (foundLocal) return this.normalizeBook(foundLocal);
    
    return null;
  },

  async createBook(book: Book) {
    const statusVal: 'completed' | 'ongoing' = book.status === 'completed' ? 'completed' : 'ongoing';
    const themesList = Array.isArray(book.themes) ? [...book.themes] : [];
    const cleanThemes = themesList.filter(t => t !== 'status:completed' && t !== 'status:ongoing');
    cleanThemes.push(`status:${statusVal}`);

    const finalBook: any = {
      author_id: book.author_id,
      title: book.title || "Untitled",
      summary: book.summary || "",
      genre: book.genre || "Fiction",
      themes: cleanThemes,
      mood: book.mood || null,
      cover_url: book.cover_url || "https://images.unsplash.com/photo-1543002588-bfa74002ed7e?w=800&auto=format&fit=crop&q=80",
      is_published: book.is_published ?? true,
      author_name: book.author_name || "Author",
      lit_isbn: book.lit_isbn || generateLitIsbn(),
    };
    
    // Only pass ID if it's a valid UUID
    if (book.id && book.id.includes('-') && book.id.length === 36) {
      finalBook.id = book.id;
    }

    if (supabase) {
      try {
        const { data: profileExists } = await supabase
          .from("profiles")
          .select("id")
          .eq("id", book.author_id)
          .maybeSingle();
        if (!profileExists) {
          console.log(
            "createBook: Profile not found for book author. Creating profile first...",
          );
          const user = await getCurrentUserInternal();
          if (user && user.id === book.author_id) {
            await this.autoCreateProfile(user);
          }
        }
      } catch (profileChkErr) {
        console.warn(
          "createBook: Profile check fallback failed, attempting upsert anyway:",
          profileChkErr,
        );
      }

      try {
        const { data, error } = await supabase
          .from("books")
          .upsert(finalBook)
          .select()
          .maybeSingle();

        if (!error && data) {
          const normalized = this.normalizeBook({ ...data, status: statusVal });
          this.saveLocalBook(normalized);
          return normalized;
        }

        if (error) {
          console.warn("createBook upsert error:", error.message);
          const fallbackBook: any = {
             author_id: finalBook.author_id,
             title: finalBook.title,
             summary: finalBook.summary,
             genre: finalBook.genre,
             themes: finalBook.themes,
             cover_url: finalBook.cover_url,
             is_published: finalBook.is_published,
             lit_isbn: finalBook.lit_isbn
          };
          if (finalBook.id) fallbackBook.id = finalBook.id;

          const { data: retryData, error: retryError } = await supabase
            .from("books")
            .upsert(fallbackBook)
            .select()
            .maybeSingle();
          if (!retryError && retryData) {
            const res = this.normalizeBook({ ...retryData, author_name: book.author_name, status: statusVal });
            this.saveLocalBook(res);
            return res;
          }
        }
      } catch (err) {
        console.warn("createBook execution error:", err);
      }
    }

    const fallbackLocalBook: Book = {
      ...book,
      id: book.id || `local_book_${Date.now()}`,
      lit_isbn: finalBook.lit_isbn,
      themes: cleanThemes,
      status: statusVal,
      created_at: new Date().toISOString(),
      total_reads: 0
    } as Book;

    this.saveLocalBook(fallbackLocalBook);
    return fallbackLocalBook;
  },

  async createChapter(chapter: Partial<Chapter>): Promise<Chapter> {
    const finalChapter: any = {
      book_id: chapter.book_id || "",
      title: chapter.title || "Untitled Chapter",
      content: chapter.content || [],
      order_index: chapter.order_index || 1,
      is_published: chapter.is_published ?? true,
      created_at: new Date().toISOString(),
    };
    if (chapter.id && chapter.id.includes('-') && chapter.id.length === 36) {
      finalChapter.id = chapter.id;
    }

    if (supabase) {
      try {
        const { data, error } = await supabase
          .from("chapters")
          .insert(finalChapter)
          .select()
          .maybeSingle();
        if (!error && data) {
          this.saveLocalChapter(data);
          return data;
        }
        if (error) {
          console.warn("createChapter DB insert warning:", error.message);
        }
      } catch (err) {
        console.warn("createChapter DB insert exception:", err);
      }
    }

    this.saveLocalChapter(finalChapter);
    return finalChapter;
  },

  async updateChapter(
    id: string,
    updates: Partial<Chapter>,
  ): Promise<Chapter | null> {
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from("chapters")
          .update(updates)
          .eq("id", id)
          .select()
          .maybeSingle();
        if (!error && data) {
          this.saveLocalChapter(data);
          return data;
        }
        if (error) {
          console.warn("updateChapter DB error:", error.message);
        }
      } catch (e) {
        console.warn("updateChapter DB exception:", e);
      }
    }

    const localChapters = this.getLocalChapters();
    const existing = localChapters.find((c) => c.id === id);
    if (existing) {
      const updated = { ...existing, ...updates };
      this.saveLocalChapter(updated);
      return updated;
    }
    return null;
  },

  async updateBookStatus(id: string, isPublished: boolean, bookStatus?: 'completed' | 'ongoing') {
    if (supabase) {
      try {
        const payload: any = { is_published: isPublished };
        if (bookStatus) {
          const { data: existing } = await supabase.from('books').select('themes').eq('id', id).maybeSingle();
          const existingThemes: string[] = Array.isArray(existing?.themes) ? existing.themes : [];
          const cleanThemes = existingThemes.filter(t => t !== 'status:completed' && t !== 'status:ongoing');
          cleanThemes.push(`status:${bookStatus}`);
          payload.themes = cleanThemes;
        }
        await supabase
          .from("books")
          .update(payload)
          .eq("id", id);
      } catch (e) {
        console.warn("updateBookStatus error:", e);
      }
    }
    const local = this.getLocalBooks().find(b => b.id === id);
    if (local) {
      local.is_published = isPublished;
      if (bookStatus) local.status = bookStatus;
      this.saveLocalBook(local);
    }
  },

  async updateBook(id: string, updates: Partial<Book>): Promise<Book | null> {
    const bookStatus = updates.status;
    if (supabase) {
      try {
        const payload: any = { ...updates };
        if (bookStatus) {
          const { data: existing } = await supabase.from('books').select('themes').eq('id', id).maybeSingle();
          const existingThemes: string[] = Array.isArray(existing?.themes) ? existing.themes : [];
          const cleanThemes = existingThemes.filter(t => t !== 'status:completed' && t !== 'status:ongoing');
          cleanThemes.push(`status:${bookStatus}`);
          payload.themes = cleanThemes;
        }
        delete payload.author_name;
        delete payload.status;
        const { data, error } = await supabase
          .from('books')
          .update(payload)
          .eq('id', id)
          .select()
          .maybeSingle();
        if (!error && data) {
          const normalized = this.normalizeBook({ ...data, status: bookStatus || (data as any).status });
          this.saveLocalBook(normalized);
          return normalized;
        }
      } catch (e) {
        console.warn('updateBook error:', e);
      }
    }
    const localBooks = this.getLocalBooks();
    const existing = localBooks.find(b => b.id === id);
    if (existing) {
      const merged = this.normalizeBook({ ...existing, ...updates });
      this.saveLocalBook(merged);
      return merged;
    }
    return null;
  },

  async getChapters(bookId: string): Promise<Chapter[]> {
    const realId = getRealId(bookId);
    const targetUuid = UUID_PATTERN.test(realId) ? realId : (UUID_PATTERN.test(bookId) ? bookId : null);
    let dbChapters: Chapter[] = [];
    if (supabase && targetUuid) {
      try {
        const { data } = await supabase
          .from("chapters")
          .select("*")
          .eq("book_id", targetUuid)
          .order("order_index", { ascending: true });
        if (data && data.length > 0) {
          dbChapters = data;
        }
      } catch (e) {
        console.warn("getChapters DB query failed:", e);
      }
    }

    const localChapters = this.getLocalChapters(bookId);
    const combined = [...localChapters, ...dbChapters];
    const deduplicated = Array.from(new Map(combined.map((c) => [c.id, c])).values())
      .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));

    return deduplicated;
  },

  async search(query: string, filters: any) {
    let dbBooks: Book[] = [];
    let dbPosts: Post[] = [];
    let dbAuthors: Profile[] = [];

    if (supabase) {
      try {
        const cleanQ = (query || "").trim();
        const q = `%${cleanQ}%`;

        // 1. Search authors / profiles
        try {
          if (cleanQ) {
            const { data: authData, error: authErr } = await supabase
              .from("profiles")
              .select("*")
              .or(`display_name.ilike.${q},full_name.ilike.${q},email.ilike.${q}`)
              .limit(25);

            if (!authErr && authData) {
              dbAuthors = authData;
            } else {
              // Fallback query if full_name column is missing
              const { data: fbAuth } = await supabase
                .from("profiles")
                .select("*")
                .or(`display_name.ilike.${q},email.ilike.${q}`)
                .limit(25);
              if (fbAuth) dbAuthors = fbAuth;
            }
          } else {
            const { data: topAuth } = await supabase
              .from("profiles")
              .select("*")
              .order("created_at", { ascending: false })
              .limit(20);
            if (topAuth) dbAuthors = topAuth;
          }
        } catch (e) {
          console.warn("search authors exception:", e);
        }

        const matchedAuthorIds = dbAuthors.map((a) => a.id).filter(Boolean);

        // 2. Search books
        try {
          let bookQuery = supabase.from("books").select("*");
          if (cleanQ) {
            let orCond = `title.ilike.${q},summary.ilike.${q},genre.ilike.${q},author_name.ilike.${q}`;
            if (matchedAuthorIds.length > 0) {
              orCond += `,author_id.in.(${matchedAuthorIds.join(",")})`;
            }
            bookQuery = bookQuery.or(orCond);
          }

          if (filters.genre && filters.genre !== "All") {
            bookQuery = bookQuery.eq("genre", filters.genre);
          }

          if (filters.sortBy === "Newest" || filters.sortBy === "newest") {
            bookQuery = bookQuery.order("created_at", { ascending: false });
          } else if (filters.sortBy === "Popular" || filters.sortBy === "popular") {
            bookQuery = bookQuery.order("total_reads", { ascending: false });
          }

          const { data: books, error: booksErr } = await bookQuery.limit(35);
          if (!booksErr && books) {
            dbBooks = books;
          }
        } catch (e) {
          console.warn("search books exception:", e);
        }

        // 3. Search posts
        try {
          let postQuery = supabase
            .from("posts")
            .select("*, profiles!fk_posts_profiles(avatar_url, display_name)");

          if (cleanQ) {
            let orCond = `content.ilike.${q},title.ilike.${q},theme.ilike.${q},author_name.ilike.${q}`;
            if (matchedAuthorIds.length > 0) {
              orCond += `,user_id.in.(${matchedAuthorIds.join(",")})`;
            }
            postQuery = postQuery.or(orCond);
          }

          if (filters.theme && filters.theme !== "All") {
            postQuery = postQuery.eq("theme", filters.theme);
          }

          if (filters.sortBy === "Newest" || filters.sortBy === "newest") {
            postQuery = postQuery.order("created_at", { ascending: false });
          } else if (filters.sortBy === "Popular" || filters.sortBy === "popular") {
            postQuery = postQuery.order("likes_count", { ascending: false });
          }

          const { data: posts, error: postsErr } = await postQuery.limit(35);

          if (!postsErr && posts && posts.length > 0) {
            dbPosts = posts.map(mapPostWithProfile);
          } else {
            // Fallback if profiles join fails due to missing foreign key relation in Supabase
            let simplePostQuery = supabase.from("posts").select("*");
            if (cleanQ) {
              let orCond = `content.ilike.${q},title.ilike.${q},theme.ilike.${q},author_name.ilike.${q}`;
              if (matchedAuthorIds.length > 0) {
                orCond += `,user_id.in.(${matchedAuthorIds.join(",")})`;
              }
              simplePostQuery = simplePostQuery.or(orCond);
            }
            if (filters.theme && filters.theme !== "All") {
              simplePostQuery = simplePostQuery.eq("theme", filters.theme);
            }
            const { data: simplePosts } = await simplePostQuery
              .order("created_at", { ascending: false })
              .limit(35);
            if (simplePosts) {
              dbPosts = simplePosts.map(mapPostWithProfile);
            }
          }
        } catch (e) {
          console.warn("search posts exception:", e);
        }
      } catch (e) {
        console.warn("Supabase search exception:", e);
      }
    }

    const term = (query || "").toLowerCase().trim();
    const localPosts = this.getLocalPosts().filter((p) => {
      if (!term) return true;
      return (
        p.title?.toLowerCase().includes(term) ||
        p.content?.toLowerCase().includes(term) ||
        p.theme?.toLowerCase().includes(term) ||
        p.author_name?.toLowerCase().includes(term)
      );
    });

    const localBooks = this.getLocalBooks().filter((b) => {
      if (!term) return true;
      return (
        b.title?.toLowerCase().includes(term) ||
        b.summary?.toLowerCase().includes(term) ||
        b.genre?.toLowerCase().includes(term) ||
        b.author_name?.toLowerCase().includes(term)
      );
    });

    const combinedPosts = Array.from(
      new Map([...dbPosts, ...localPosts].map((p) => [p.id, p])).values()
    );
    const combinedBooks = Array.from(
      new Map([...dbBooks, ...localBooks].map((b) => [b.id, b])).values()
    );

    return {
      books: combinedBooks,
      posts: combinedPosts,
      authors: dbAuthors,
    };
  },

  async getUserPublishedPosts(userId: string): Promise<Post[]> {
    let dbPosts: Post[] = [];
    let targetId = userId;
    
    if (userId === "me" || !userId) {
      const user = await getCurrentUserInternal();
      if (user) targetId = user.id;
    } else if (!targetId.includes("-")) {
      const profile = await this.getProfile(userId);
      if (profile && profile.id) targetId = profile.id;
    }

    if (supabase && targetId) {
      try {
        const { data, error } = await supabase
          .from("posts")
          .select("*, profiles!fk_posts_profiles(avatar_url, display_name)")
          .eq("user_id", targetId)
          .order("created_at", { ascending: false });
          
        if (!error && data && data.length > 0) {
          dbPosts = data.map(mapPostWithProfile);
        } else {
          const { data: simpleData } = await supabase
            .from("posts")
            .select("*")
            .eq("user_id", targetId)
            .order("created_at", { ascending: false });
          if (simpleData) {
            dbPosts = simpleData.map(mapPostWithProfile);
          }
        }
      } catch (err) {
        console.warn("getUserPublishedPosts error:", err);
      }
    }

    const serverPosts = await fetchServerPosts({ user_id: targetId });
    const localPosts = this.getLocalPosts().filter(p => p.user_id === targetId || p.user_id === userId);
    const combined = [...localPosts, ...serverPosts, ...dbPosts].map(p => ({
      ...p,
      author_name: cleanAuthorName(p.author_name)
    }));

    return Array.from(new Map(combined.map((p) => [p.id, p])).values()).sort(
      (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
    );
  },

  async getUserPublishedBooks(userId: string): Promise<Book[]> {
    let dbBooks: Book[] = [];
    let targetId = userId;
    
    if (userId === "me" || !userId) {
      const user = await getCurrentUserInternal();
      if (user) targetId = user.id;
    } else if (!targetId.includes("-")) {
      const profile = await this.getProfile(userId);
      if (profile && profile.id) targetId = profile.id;
    }

    if (supabase && targetId) {
      try {
        let { data, error } = await supabase
          .from("books")
          .select("*")
          .eq("author_id", targetId)
          .order("created_at", { ascending: false });

        if (error || !data || data.length === 0) {
          // Fallback query checking user_id column
          const { data: userBooks } = await supabase
            .from("books")
            .select("*")
            .eq("user_id", targetId)
            .order("created_at", { ascending: false });
          if (userBooks && userBooks.length > 0) data = userBooks;
        }

        if (data) {
          dbBooks = data;
          for (const b of dbBooks) {
            try {
              const { count } = await supabase.from('likes').select('*', { count: 'exact', head: true }).eq('book_id', b.id);
              if (count !== null && count > 0) b.likes_count = Math.max(b.likes_count || 0, count);
            } catch (e) {}
          }
        }
      } catch (err) {
        console.warn("getUserPublishedBooks error:", err);
      }
    }
    const localBooks = this.getLocalBooks().filter(b => b.author_id === targetId || (b as any).user_id === targetId || b.author_id === userId);
    const combined = [...localBooks, ...dbBooks].map(b => ({
      ...b,
      author_name: cleanAuthorName(b.author_name)
    }));
    return Array.from(new Map(combined.map((b) => [b.id, b])).values());
  },

  async getLikeCount(
    id: string,
    resourceType: "post" | "book" | "chapter" = "post",
  ): Promise<number | null> {
    const realId = getRealId(id);
    let count: number | null = null;
    if (supabase) {
      try {
        const query = supabase
          .from("likes")
          .select("*", { count: "exact", head: true });
        if (resourceType === "post") query.eq("post_id", realId);
        else if (resourceType === "book") query.eq("book_id", realId);
        else if (resourceType === "chapter") query.eq("chapter_id", realId);

        const { count: dbCount, error } = await query;
        if (!error && dbCount !== null) {
          count = dbCount;
        }
      } catch (e) {
        console.warn("getLikeCount error:", e);
      }
    }

    // Check local storage post or book to prevent wiping higher non-zero likes_count
    if (resourceType === "post") {
      const local = this.getLocalPosts().find((p) => p.id === id || p.id === realId);
      const localCount = local?.likes_count || 0;
      return Math.max(count || 0, localCount);
    } else if (resourceType === "book") {
      const local = this.getLocalBooks().find((b) => b.id === id || b.id === realId);
      const localCount = local?.likes_count || 0;
      return Math.max(count || 0, localCount);
    }

    return count;
  },

  async isLiked(
    id: string,
    resourceType: "post" | "book" | "chapter" = "post",
  ): Promise<boolean> {
    const realId = getRealId(id);
    // Check local storage key
    const localKey = `literally_liked_${resourceType}s`;
    const likedIds = JSON.parse(localStorage.getItem(localKey) || "[]");
    if (likedIds.includes(id) || likedIds.includes(realId)) return true;

    if (supabase) {
      try {
        const user = await getCurrentUserInternal();
        if (!user) return false;

        const query = supabase
          .from("likes")
          .select("*", { count: "exact", head: true })
          .eq("user_id", user.id);
        if (resourceType === "post") query.eq("post_id", realId);
        else if (resourceType === "book") query.eq("book_id", realId);
        else if (resourceType === "chapter") query.eq("chapter_id", realId);

        const { count, error } = await query;
        if (error) {
          console.warn("isLiked query notice:", error.message || error);
          return false;
        }
        const isDbLiked = (count || 0) > 0;
        if (isDbLiked && !likedIds.includes(realId)) {
          likedIds.push(realId);
          localStorage.setItem(localKey, JSON.stringify(likedIds));
        }
        return isDbLiked;
      } catch (e) {
        console.warn("isLiked DB issue, using local cache:", e);
      }
    }
    return false;
  },

  async isFollowing(targetUserId: string): Promise<boolean> {
    if (supabase) {
      try {
        const user = await getCurrentUserInternal();
        if (!user) return false;

        const { count, error } = await supabase
          .from("follows")
          .select("*", { count: "exact", head: true })
          .eq("follower_id", user.id)
          .eq("following_id", targetUserId);
        if (error) return false;
        return (count || 0) > 0;
      } catch (e) {
        return false;
      }
    }
    return false;
  },

   async getFollowing(userId?: string): Promise<Profile[]> {
    if (supabase) {
      let id = userId;
      if (!id || id === "me") {
        const user = await getCurrentUserInternal();
        if (!user) return [];
        id = user.id;
      }

      try {
        const { data: followsData, error: followsError } = await supabase
          .from("follows")
          .select("following_id")
          .eq("follower_id", id);

        if (followsError) {
          if (followsError.message?.includes("permission denied")) isRlsMisconfigured = true;
          const { data, error } = await supabase
            .from("follows")
            .select("profiles!follows_following_id_fkey(*)")
            .eq("follower_id", id);
          if (error) {
            if (error.message?.includes("permission denied")) isRlsMisconfigured = true;
            return [];
          }
          return (data || []).map((f: any) => f.profiles).filter(Boolean);
        }

        const followingIds = (followsData || []).map((f: any) => f.following_id).filter(Boolean);
        if (followingIds.length === 0) return [];

        const { data: profilesData, error: profilesError } = await supabase
          .from("profiles")
          .select("*")
          .in("id", followingIds);

        if (profilesError) {
          return [];
        }

        return profilesData || [];
      } catch (e) {
        return [];
      }
    }
    return [];
  },

  async getFollowers(userId?: string): Promise<Profile[]> {
    if (supabase) {
      let id = userId;
      if (!id || id === "me") {
        const user = await getCurrentUserInternal();
        if (!user) return [];
        id = user.id;
      }

      try {
        const { data: followsData, error: followsError } = await supabase
          .from("follows")
          .select("follower_id")
          .eq("following_id", id);

        if (followsError) {
          if (followsError.message?.includes("permission denied")) isRlsMisconfigured = true;
          const { data, error } = await supabase
            .from("follows")
            .select("profiles!follows_follower_id_fkey(*)")
            .eq("following_id", id);
          if (error) {
            if (error.message?.includes("permission denied")) isRlsMisconfigured = true;
            return [];
          }
          return (data || []).map((f: any) => f.profiles).filter(Boolean);
        }

        const followerIds = (followsData || []).map((f: any) => f.follower_id).filter(Boolean);
        if (followerIds.length === 0) return [];

        const { data: profilesData, error: profilesError } = await supabase
          .from("profiles")
          .select("*")
          .in("id", followerIds);

        if (profilesError) {
          return [];
        }

        return profilesData || [];
      } catch (e) {
        return [];
      }
    }
    return [];
  },

  async spendInkDropOnChapter() {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) throw new Error("Not logged in");

      const { error } = await supabase.rpc("deduct_ink", {
        amount: 1,
        user_id: user.id,
      });
      if (error) throw error;
    } else {
      throw new Error("Supabase not configured.");
    }
  },

  async topUpInk(amount: number) {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) throw new Error("Not logged in");

      const { error } = await supabase.rpc("add_ink_to_profile", {
        amount,
        user_id: user.id,
      });
      if (error) throw error;
    } else {
      throw new Error("Supabase not configured.");
    }
  },

  subscribeToNotifications(callback: any): () => void {
    if (supabase) {
      let cancelled = false;
      let channel: any = null;

      // We need the user ID to filter notifications
      getCurrentUserInternal().then((user) => {
        if (cancelled || !user) return;

        const topic = `notifications_${user.id}_${Math.random().toString(36).substring(2, 7)}`;
        channel = supabase
          .channel(topic)
          .on(
            "postgres_changes",
            {
              event: "INSERT",
              schema: "public",
              table: "notifications",
              filter: `user_id=eq.${user.id}`,
            },
            (payload: any) => {
              callback(payload.new);
            },
          )
          .subscribe();

        if (cancelled && channel) {
          supabase.removeChannel(channel);
        }
      });

      return () => {
        cancelled = true;
        if (channel) supabase.removeChannel(channel);
      };
    }
    return () => {};
  },

  onAuthStateChange(callback: (event: any, session: any) => void) {
    if (supabase) {
      return supabase.auth.onAuthStateChange(callback);
    }
    return { data: { subscription: { unsubscribe: () => {} } } };
  },

  async signOut(): Promise<void> {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem("literally-auth-token");
      try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.includes("auth-token") || key.includes("supabase.auth"))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(k => localStorage.removeItem(k));
      } catch (e: any) {
        console.warn("Error running localStorage cleanup", e);
      }
    }

    currentUserCache = null;
    Object.keys(resolvedProfileCache).forEach((key) => {
      delete resolvedProfileCache[key];
    });

    if (supabase) {
      await supabase.auth.signOut().catch((e: any) => {
        console.warn("Supabase auth.signOut failed, proceeding with local clean out:", e);
      });
    }
  },

  async deactivateAccount(): Promise<void> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (user) {
        // Soft delete or flag status as inactive in case of foreign keys or hard cascade if rules exist
        const { error } = await supabase
          .from("profiles")
          .update({ is_active: false })
          .eq("id", user.id);
        if (error) {
          console.warn(
            "Failed soft deactivate profiles, attempting cascade delete:",
            error,
          );
          await supabase.from("profiles").delete().eq("id", user.id);
        }
        // Sign out from browser session
        await this.signOut();
      }
    }
  },

  async signUp(
    fullName: string,
    email: string,
    password: string,
    dateOfBirth: string,
    penName?: string,
  ): Promise<{ needsVerification: boolean; session?: any }> {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error("Please enter a valid email address.");
    }

    if (!supabase) throw new Error("Supabase not configured.");

    console.log("supabaseService: signUp starting...");
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: typeof window !== 'undefined' ? window.location.origin : undefined,
          data: {
            full_name: fullName,
            display_name:
              penName || `User_${Math.random().toString(36).substring(2, 10)}`,
            date_of_birth: dateOfBirth,
          },
        },
      });

      if (error) {
        console.error("supabaseService: signUp error:", error.message);
        throw error;
      }

      
      currentUserCache = null;
      currentUserPromise = null;
      Object.keys(resolvedProfileCache).forEach((key) => {
        delete resolvedProfileCache[key];
      });

      console.log("supabaseService: signUp successful");
      return { needsVerification: !data.session, session: data.session };
    } catch (err: any) { throw err; }
  },

  async login(email: string, password: string): Promise<void> {
    if (!supabase) throw new Error("Supabase not configured.");

    console.log("supabaseService: login starting with URL:", supabaseUrl);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error("supabaseService: login error:", error.message);
        throw error;
      }

      
      currentUserCache = null;
      currentUserPromise = null;
      Object.keys(resolvedProfileCache).forEach((key) => {
        delete resolvedProfileCache[key];
      });

      console.log("supabaseService: login successful");
    } catch (err: any) { throw err; }
  },

  async verifyEmail(email: string, code: string): Promise<any> {
    if (!supabase) throw new Error("Supabase not configured.");

    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token: code,
      type: "signup",
    });

    if (error) throw error;
    return data;
  },

  async resendVerificationEmail(email: string): Promise<void> {
    if (!supabase) throw new Error("Supabase not configured.");

    const { error } = await supabase.auth.resend({
      type: "signup",
      email: email,
    });

    if (error) throw error;
  },

  async signInWithGoogle(): Promise<void> {
    if (!supabase) throw new Error("Supabase not configured.");

    const isIframe = window !== window.top;
    
    if (isIframe) {
      // AI Studio Preview specific handling
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.origin + '/auth/callback',
          skipBrowserRedirect: true,
          // Force the account chooser instead of the consent screen. It lists
          // accounts that have already signed in on this device, so a returning
          // user just taps their name instead of retyping an email/password.
          queryParams: { prompt: 'select_account' },
        },
      });

      if (error) throw error;
      
      if (data && data.url) {
        window.open(data.url, 'oauth_popup', 'width=500,height=600');
        
        return new Promise((resolve, reject) => {
          const handler = async (event: MessageEvent) => {
            if (event.data && event.data.type === 'OAUTH_AUTH_SUCCESS') {
              window.removeEventListener('message', handler);
              const hash = event.data.hash;
              
              if (!hash) {
                // Sometimes it's in the query params if implicit flow is off
                return resolve(); 
              }

              // Parse hash #access_token=...&refresh_token=...
              // or query ?code=...
              const params = new URLSearchParams(hash.startsWith('#') || hash.startsWith('?') ? hash.substring(1) : hash);
              const access_token = params.get('access_token');
              const refresh_token = params.get('refresh_token');
              
              const code = params.get('code');
              if (code) {
                const { error: sessionErr } = await supabase.auth.exchangeCodeForSession(code);
                if (sessionErr) reject(sessionErr);
                else resolve();
              } else if (access_token && refresh_token) {
                const { error: sessionErr } = await supabase.auth.setSession({ access_token, refresh_token });
                if (sessionErr) reject(sessionErr);
                else resolve();
              } else {
                // If it's a code flow, supabase handles it via exchangeCodeForSession?
                // Actually, if it's hash, it's tokens. 
                resolve();
              }
            }
          };
          window.addEventListener('message', handler);
        });
      }
    } else {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.origin,
          // Show Google's account chooser so returning users can just tap an
          // account that is already signed in on the device.
          queryParams: { prompt: 'select_account' },
        },
      });

      if (error) throw error;
    }
  },

  async updateBankDetails(details: BankDetails): Promise<void> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (user) {
        await supabase
          .from("profiles")
          .update({ bank_details: details })
          .eq("id", user.id);
      }
    } else {
      throw new Error("Not logged in");
    }
  },

  // Competition rich metadata helper routines
  getLocalCompetitionMeta(idOrHashtag: string): Partial<Competition> | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = localStorage.getItem(`literally_comp_meta_${idOrHashtag}`);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },

  saveLocalCompetitionMeta(idOrHashtag: string, meta: Partial<Competition>): void {
    if (typeof window === 'undefined') return;
    try {
      const existing = this.getLocalCompetitionMeta(idOrHashtag) || {};
      localStorage.setItem(`literally_comp_meta_${idOrHashtag}`, JSON.stringify({ ...existing, ...meta }));
    } catch {}
  },

  enrichCompetition(comp: any): Competition {
    if (!comp) return comp;
    const localMeta = this.getLocalCompetitionMeta(comp.id) || this.getLocalCompetitionMeta(comp.hashtag) || {};
    const votingType = comp.voting_type || localMeta.voting_type || (comp.allow_ink_voting === false ? 'likes' : 'ink');
    const allowInkVoting = comp.allow_ink_voting !== undefined ? comp.allow_ink_voting : (localMeta.allow_ink_voting !== undefined ? localMeta.allow_ink_voting : (votingType !== 'likes'));
    const closeDate = comp.close_date || localMeta.close_date || comp.end_date;
    const announcementDate = comp.announcement_date || localMeta.announcement_date || (comp.end_date ? new Date(new Date(comp.end_date).getTime() + 7 * 86400000).toISOString() : undefined);

    const now = Date.now();
    const startTime = comp.start_date ? new Date(comp.start_date).getTime() : Number.NaN;
    const endTime = comp.end_date ? new Date(comp.end_date).getTime() : Number.NaN;
    const originalStatus = String(comp.status || '').trim().toLowerCase();
    let effectiveStatus = originalStatus || 'upcoming';

    if (!['deleted', 'archived', 'completed', 'ended', 'closed'].includes(originalStatus)) {
      if (Number.isFinite(endTime) && now > endTime) {
        effectiveStatus = 'ended';
      } else if (Number.isFinite(startTime) && now < startTime) {
        effectiveStatus = 'upcoming';
      } else if (
        (Number.isFinite(startTime) && now >= startTime) ||
        (Number.isFinite(endTime) && now <= endTime) ||
        ['open', 'ongoing', 'live', 'running', 'in_progress'].includes(originalStatus)
      ) {
        effectiveStatus = 'active';
      } else if (['scheduled', 'pending', 'not_started'].includes(originalStatus)) {
        effectiveStatus = 'upcoming';
      }
    }

    return {
      ...comp,
      status: effectiveStatus,
      type: comp.type || 'poem',
      close_date: closeDate,
      announcement_date: announcementDate,
      prompt: comp.prompt || localMeta.prompt || comp.description || '',
      voting_type: votingType,
      allow_ink_voting: allowInkVoting,
      is_free: comp.is_free !== undefined ? comp.is_free : (comp.entry_fee === 0 || !comp.entry_fee),
      entry_fee: comp.entry_fee ?? 0,
      prize_pool: comp.prize_pool ?? 0,
      prizes: comp.prizes || localMeta.prizes || (comp.prize_pool ? [
        { place: '1st Place', reward: `₦${Math.round(comp.prize_pool * 0.5).toLocaleString()} + Verified Badge + Front Page Feature` },
        { place: '2nd Place', reward: `₦${Math.round(comp.prize_pool * 0.3).toLocaleString()} + Official Recognition` },
        { place: '3rd Place', reward: `₦${Math.round(comp.prize_pool * 0.2).toLocaleString()} + Ink Drop Boost` },
      ] : [
        { place: '1st Place', reward: 'Gold Winner Trophy + Featured Author Spotlight' },
        { place: '2nd Place', reward: 'Silver Trophy + Community Recognition' },
        { place: '3rd Place', reward: 'Bronze Trophy + Hall of Fame Placement' },
      ]),
      rules: comp.rules || localMeta.rules || 'Submissions must be 100% original. Writers may enter one work per competition.',
      requirements: comp.requirements || localMeta.requirements || 'All registered writers are eligible. Entries must match the specified competition format (Poem, Book, or Essay).',
      stages: comp.stages || localMeta.stages || [],
      current_stage_id: comp.current_stage_id || localMeta.current_stage_id || (comp.stages?.[0]?.id),
      current_stage_index: comp.current_stage_index || localMeta.current_stage_index || 0,
    };
  },

  async syncLocalCompetitionToServer(comp: any): Promise<void> {
    try {
      let token: string | undefined;
      if (supabase) {
        const { data } = await supabase.auth.getSession();
        token = data?.session?.access_token;
      }
      await fetch('/api/competitions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(comp)
      });
    } catch (e) {
      // background silent sync
    }
  },

  async getAllCompetitions(): Promise<Competition[]> {
    let comps: Competition[] = [];
    let apiReadFailed = false;
    let databaseReadFailed = !supabase;

    // Read the shared table first so an unavailable serverless endpoint cannot
    // hide competitions that the authenticated database client can read.
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from("competitions")
          .select("*")
          .order("created_at", { ascending: false });
        if (error) {
          databaseReadFailed = true;
          console.warn("getAllCompetitions DB query error:", error);
        } else if (data) {
          comps = data;
        }
      } catch (e) {
        databaseReadFailed = true;
        console.warn("getAllCompetitions DB query error:", e);
      }
    }

    if (databaseReadFailed) {
      try {
        const res = await fetch('/api/competitions', {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' },
        });
        if (res.ok) {
          const json = await res.json();
          if (Array.isArray(json.competitions)) {
            comps = json.competitions;
          } else {
            apiReadFailed = true;
            console.warn('getAllCompetitions API returned an invalid response.');
          }
        } else {
          apiReadFailed = true;
          console.warn(`getAllCompetitions API returned ${res.status}.`);
        }
      } catch (e) {
        apiReadFailed = true;
        console.warn('getAllCompetitions API fetch error:', e);
      }
    }

    if (apiReadFailed && databaseReadFailed && comps.length === 0) {
      throw new Error('Unable to load competitions from the server or database.');
    }

    const now = Date.now();
    return comps
      .filter(c => c.status !== 'deleted' && c.status !== 'archived')
      .map(c => {
        const status = String(c.status || '').trim().toLowerCase();
        const start = c.start_date ? new Date(c.start_date).getTime() : Number.NaN;
        const end = c.end_date ? new Date(c.end_date).getTime() : Number.NaN;
        let effectiveStatus = status || 'upcoming';

        if (!['completed', 'ended', 'closed'].includes(status)) {
          if (Number.isFinite(end) && now > end) {
            effectiveStatus = 'ended';
          } else if (Number.isFinite(start) && now < start) {
            effectiveStatus = 'upcoming';
          } else if (
            (Number.isFinite(start) && now >= start) ||
            (Number.isFinite(end) && now <= end) ||
            ['active', 'open', 'ongoing', 'live', 'running', 'in_progress'].includes(status)
          ) {
            effectiveStatus = 'active';
          } else if (['scheduled', 'pending', 'not_started'].includes(status)) {
            effectiveStatus = 'upcoming';
          }
        }

        return this.enrichCompetition({ ...c, status: effectiveStatus });
      });
  },
  async updatePayoutStatus(id: string, status: string): Promise<void> {
    if (supabase) {
      if (status === "rejected") {
        const { data: payout } = await supabase.from("payout_transactions").select("*").eq("id", id).single();
        if (payout) {
          const { data: profile } = await supabase.from("profiles").select("monthly_read_time, total_earnings").eq("id", payout.user_id).single();
          if (profile) {
            const refundedMinutes = Math.floor(payout.amount / 2.5);
            await supabase.from("profiles").update({ 
                monthly_read_time: (profile.monthly_read_time || 0) + refundedMinutes,
                total_earnings: Math.max(0, (profile.total_earnings || 0) - payout.amount)
            }).eq("id", payout.user_id);
          }
        }
      }
      const { error } = await supabase.from("payout_transactions").update({ status }).eq("id", id);
      if (error) throw error;
    }
  },
  
  async approveUserEarnings(userId: string, amount: number): Promise<void> {
    if (supabase) {
      const { data: profile } = await supabase.from("profiles").select("wallet_balance, total_earnings").eq("id", userId).single();
      if (!profile) throw new Error("Profile not found");
      
      const { error } = await supabase.from("profiles").update({
        wallet_balance: (profile.wallet_balance || 0) + amount,
        total_earnings: (profile.total_earnings || 0) + amount,
        monthly_read_time: 0,
        ink_drop_balance: 0,
        book_reads: 0,
      }).eq("id", userId);
      if (error) throw error;
    }
  },
  async createCompetition(
    comp: Partial<Competition>,
  ): Promise<Competition | undefined> {
    const isFree = comp.is_free !== undefined ? comp.is_free : (comp.entry_fee === 0 || !comp.entry_fee);
    const entryFee = isFree ? 0 : (comp.entry_fee || 0);
    const prizePool = comp.prize_pool || 0;
    
    // Default stages if none provided
    const stages = comp.stages && comp.stages.length ? comp.stages : [
      {
        id: `stage-1-${Date.now()}`,
        name: 'Stage 1: Open Submissions',
        description: 'Writers submit their entries. Readers explore and vote with Ink Drops.',
        status: 'active' as const,
        start_date: comp.start_date || new Date().toISOString(),
        end_date: comp.end_date || new Date(Date.now() + 14 * 86400000).toISOString(),
        advancement_criteria: 'Top voted & editorially screened entries advance to the Shortlist.',
        advanced_entry_ids: []
      },
      {
        id: `stage-2-${Date.now()}`,
        name: 'Stage 2: Shortlist & Semifinals',
        description: 'Shortlisted qualifiers compete for community ratings and jury deliberation.',
        status: 'upcoming' as const,
        start_date: comp.end_date || new Date(Date.now() + 14 * 86400000).toISOString(),
        end_date: new Date(Date.now() + 21 * 86400000).toISOString(),
        advancement_criteria: 'Top 5 entries advance to the Grand Finale.',
        advanced_entry_ids: []
      },
      {
        id: `stage-3-${Date.now()}`,
        name: 'Stage 3: Grand Finale & Winner Announcement',
        description: 'Finalists are judged. Crown winners and distribute prize rewards.',
        status: 'upcoming' as const,
        start_date: new Date(Date.now() + 21 * 86400000).toISOString(),
        end_date: new Date(Date.now() + 28 * 86400000).toISOString(),
        advancement_criteria: 'Overall champion and prize podium decided.',
        advanced_entry_ids: []
      }
    ];

    const richMeta = {
      is_free: isFree,
      entry_fee: entryFee,
      close_date: comp.close_date || comp.end_date || new Date(Date.now() + 14 * 86400000).toISOString(),
      announcement_date: comp.announcement_date || (comp.end_date ? new Date(new Date(comp.end_date).getTime() + 7 * 86400000).toISOString() : new Date(Date.now() + 21 * 86400000).toISOString()),
      prompt: comp.prompt || comp.description,
      voting_type: comp.voting_type || (comp.allow_ink_voting === false ? 'likes' : 'ink'),
      allow_ink_voting: comp.allow_ink_voting !== undefined ? comp.allow_ink_voting : (comp.voting_type !== 'likes'),
      prizes: comp.prizes,
      rules: comp.rules,
      requirements: comp.requirements,
      stages,
      current_stage_id: stages[0].id,
      current_stage_index: 0,
    };

    const baseRow: any = {
      title: comp.title,
      description: comp.description,
      hashtag: comp.hashtag?.startsWith('#') ? comp.hashtag : `#${comp.hashtag || 'contest'}`,
      entry_fee: entryFee,
      prize_pool: prizePool,
      start_date: comp.start_date || new Date().toISOString(),
      end_date: comp.end_date || new Date(Date.now() + 14 * 86400000).toISOString(),
      close_date: richMeta.close_date,
      announcement_date: richMeta.announcement_date,
      type: comp.type || 'poem',
      status: comp.status || 'upcoming',
      entries_count: 0,
    };

    const fullInsert = { ...baseRow, ...richMeta };
    let createdData: any = null;
    let databaseError = '';

    if (supabase) {
      try {
        const insertPayload: Record<string, unknown> = { ...fullInsert };
        const maxAttempts = Object.keys(insertPayload).length + 1;
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          const { data, error } = await supabase
            .from("competitions")
            .insert(insertPayload)
            .select()
            .maybeSingle();
          if (!error) {
            createdData = data;
            break;
          }

          databaseError = error.message || 'Database insert failed.';
          const missingColumn = getMissingCompetitionColumn(error);
          const requiredFields = ['title', 'start_date', 'end_date', 'type'];
          if (
            !missingColumn ||
            !(missingColumn in insertPayload) ||
            requiredFields.includes(missingColumn)
          ) {
            break;
          }

          console.warn(
            `Competition insert retry without unsupported "${missingColumn}" column.`,
          );
          delete insertPayload[missingColumn];
        }
      } catch (error) {
        databaseError = error instanceof Error ? error.message : String(error);
        console.warn('Direct competition insert failed; trying the server endpoint.', error);
      }
    }

    let apiError = '';

    // Fall back to the server API when the authenticated database write failed.
    if (!createdData) {
      try {
      let token: string | undefined;
      if (supabase) {
        const { data } = await supabase.auth.getSession();
        token = data?.session?.access_token;
      }
      const res = await fetch('/api/competitions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(fullInsert)
      });
      const json = await res.json().catch(() => null);
      if (res.ok && json?.competition) {
        createdData = json.competition;
      } else {
        apiError = json?.error || `Competition API returned ${res.status}.`;
        console.warn(`createCompetition API insert returned ${res.status}.`);
      }
      } catch (apiErr) {
        apiError = apiErr instanceof Error ? apiErr.message : 'Competition API request failed.';
        console.warn("createCompetition API insert failed:", apiErr);
      }
    }

    if (!createdData) {
      const details = [databaseError, apiError].filter(Boolean).join(' ');
      throw new Error(`Competition could not be saved to the shared database.${details}`);
    }

    if (createdData?.id) {
      this.saveLocalCompetitionMeta(createdData.id, richMeta);
      this.saveLocalCompetitionMeta(baseRow.hashtag, richMeta);
    }

    if (typeof window !== 'undefined') {
      try {
        window.dispatchEvent(new CustomEvent('competition-updated'));
      } catch {}
    }

    return this.enrichCompetition(createdData);
  },
  async updateCompetition(
    id: string,
    updates: Partial<Competition>,
  ): Promise<Competition | undefined> {
    let updatedData: any = null;
    let updateError = '';

    // 1. Try the server API, which verifies the admin and forwards their JWT to RLS.
    try {
      let token: string | undefined;
      if (supabase) {
        const { data } = await supabase.auth.getSession();
        token = data?.session?.access_token;
      }
      const res = await fetch('/api/competitions', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ ...updates, id })
      });
      if (res.ok) {
        const json = await res.json();
        if (json.competition) {
          updatedData = json.competition;
        }
      } else {
        const json = await res.json().catch(() => null);
        updateError = json?.error || `Competition API returned ${res.status}.`;
        console.warn(`updateCompetition API returned ${res.status}; trying direct Supabase.`);
      }
    } catch (apiErr) {
      updateError = apiErr instanceof Error ? apiErr.message : String(apiErr);
      console.warn("updateCompetition API update failed, falling back to direct DB:", apiErr);
    }

    // 2. Direct Supabase client update
    if (!updatedData && supabase) {
      try {
        const updatePayload: Record<string, unknown> = { ...updates };
        const maxAttempts = Object.keys(updatePayload).length + 1;
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          const { data, error } = await supabase
            .from("competitions")
            .update(updatePayload)
            .eq("id", id)
            .select()
            .maybeSingle();
          if (!error) {
            updatedData = data;
            break;
          }

          updateError = error.message || 'Database update failed.';
          const missingColumn = getMissingCompetitionColumn(error);
          if (!missingColumn || !(missingColumn in updatePayload)) break;

          console.warn(
            `Competition update retry without unsupported "${missingColumn}" column.`,
          );
          delete updatePayload[missingColumn];
        }
      } catch (e) {
        updateError = e instanceof Error ? e.message : String(e);
        console.warn("updateCompetition DB error:", e);
      }
    }

    if (!updatedData) {
      throw new Error(updateError || 'Competition could not be updated in the shared database.');
    }

    this.saveLocalCompetitionMeta(id, updates);

    if (typeof window !== 'undefined') {
      try {
        const localList = JSON.parse(localStorage.getItem('literally_local_competitions') || '[]');
        const exists = localList.some((c: any) => c.id === id);
        let updatedList;
        if (exists) {
          updatedList = localList.map((c: any) => c.id === id ? { ...c, ...updates } : c);
        } else {
          updatedList = [{ id, ...updates }, ...localList];
        }
        localStorage.setItem('literally_local_competitions', JSON.stringify(updatedList));
        window.dispatchEvent(new CustomEvent('competition-updated'));
      } catch {}
    }

    return this.enrichCompetition({ ...updatedData, ...updates });
  },

  async deleteCompetition(id: string): Promise<boolean> {
    // 1. Call the server API with the signed-in admin session.
    try {
      let token: string | undefined;
      if (supabase) {
        const { data } = await supabase.auth.getSession();
        token = data?.session?.access_token;
      }
      await fetch(`/api/competitions?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch (apiErr) {
      console.warn("deleteCompetition API delete warning:", apiErr);
    }

    // 2. Also try direct client Supabase delete
    if (supabase) {
      try {
        await supabase.from("competition_entries").delete().eq("competition_id", id);
        await supabase.from("competitions").update({ is_active: false, status: 'deleted' }).eq("id", id);
        await supabase.from("competitions").delete().eq("id", id);
      } catch (e) {
        console.warn("deleteCompetition DB error:", e);
      }
    }

    // 3. Purge from local storage caches
    if (typeof window !== 'undefined') {
      try {
        const localList = JSON.parse(localStorage.getItem('literally_local_competitions') || '[]');
        const filtered = localList.filter((c: any) => c.id !== id);
        localStorage.setItem('literally_local_competitions', JSON.stringify(filtered));
        localStorage.removeItem(`literally_comp_meta_${id}`);
        localStorage.removeItem(`comp_entries_${id}`);

        const deletedIds = JSON.parse(localStorage.getItem('literally_deleted_competitions') || '[]');
        if (!deletedIds.includes(id)) {
          deletedIds.push(id);
          localStorage.setItem('literally_deleted_competitions', JSON.stringify(deletedIds));
        }

        window.dispatchEvent(new CustomEvent('competition-updated'));
      } catch {}
    }
    return true;
  },
  async getCompetitionById(id: string): Promise<Competition | null> {
    if (supabase) {
      try {
        const { data } = await supabase
          .from("competitions")
          .select("*")
          .eq("id", id)
          .maybeSingle();
        if (data) return this.enrichCompetition(data);
      } catch (e) {
        console.warn("getCompetitionById error:", e);
      }
    }
    const all = await this.getAllCompetitions();
    const found = all.find(c => c.id === id);
    return found ? this.enrichCompetition(found) : null;
  },
  async getPost(id: string): Promise<Post | null> {
    const realId = getRealId(id);
    let dbPost: Post | null = null;
    let lookupError: unknown = null;

    if (supabase) {
      try {
        const targetUuid = UUID_PATTERN.test(realId) ? realId : (UUID_PATTERN.test(id) ? id : null);
        if (targetUuid) {
          let postData: any = null;
          // Try join with explicit foreign key relationship and valid columns only
          try {
            const joinResult = await withTimeout(
              supabase
                .from("posts")
                .select("*, profiles!fk_posts_profiles(avatar_url, display_name, full_name)")
                .eq("id", targetUuid)
                .maybeSingle() as any,
              5000,
              "getPost DB join timeout"
            ) as any;
            if (joinResult && !joinResult.error && joinResult.data) {
              postData = joinResult.data;
            }
          } catch (joinErr) {
            console.warn("getPost join error, falling back to direct select:", joinErr);
          }

          // Resilient fallback: direct query on posts table
          if (!postData) {
            const plainResult = await withTimeout(
              supabase
                .from("posts")
                .select("*")
                .eq("id", targetUuid)
                .maybeSingle() as any,
              5000,
              "getPost DB plain timeout"
            ) as any;
            if (plainResult && !plainResult.error && plainResult.data) {
              postData = plainResult.data;
              if (postData.user_id && !postData.profiles) {
                try {
                  const { data: prof } = await supabase
                    .from("profiles")
                    .select("avatar_url, display_name, full_name")
                    .eq("id", postData.user_id)
                    .maybeSingle();
                  if (prof) {
                    postData.profiles = prof;
                  }
                } catch {
                  // Non-fatal profile lookup error
                }
              }
            }
          }

          if (postData) {
            dbPost = mapPostWithProfile(postData);
            lookupError = null;
          }
        }
      } catch (e) {
        console.warn("getPost DB error:", e);
        lookupError = e;
      }
    }

    // Try server API if not resolved from DB
    if (!dbPost) {
      try {
        const queryId = UUID_PATTERN.test(realId) ? realId : (UUID_PATTERN.test(id) ? id : (realId || id));
        const res = await fetch(`/api/posts/${queryId}`);
        if (res.ok) {
          const serverPost = await res.json();
          if (serverPost) {
            dbPost = mapPostWithProfile(serverPost);
            lookupError = null;
          }
        } else if (res.status === 404) {
          lookupError = null;
        } else if (res.status >= 500) {
          lookupError = new Error(`Post service returned ${res.status}.`);
        }
      } catch (e) {
        console.warn("getPost API fallback error:", e);
      }
    }

    if (dbPost) return dbPost;

    // Check local storage posts fallback
    const localPosts = this.getLocalPosts();
    const foundLocal = localPosts.find((p) => p.id === id || p.id === realId);
    if (foundLocal) return foundLocal;

    if (lookupError && !dbPost) {
      throw new Error("Couldn't load this post. Check your connection and try again.");
    }

    return null;
  },
  async getMentionSuggestions(query: string): Promise<Profile[]> {
    if (supabase) {
      const { data } = await supabase
        .from("profiles")
        .select(STABLE_PROFILE_COLUMNS)
        .ilike("display_name", `%${query}%`)
        .limit(5);
      return data || [];
    }
    return [];
  },

  async getActiveCompetition(): Promise<Competition | null> {
    const all = await this.getActiveCompetitions();
    if (all.length > 0) {
      const active = all.find(c => c.status === 'active');
      return active ? active : all[0];
    }
    return null;
  },

  async getActiveCompetitions(): Promise<Competition[]> {
    const all = await this.getAllCompetitions();
    return all.filter(c => c.status === 'active' || c.status === 'upcoming');
  },

  /**
   * Determine if the contest link should be visible in the navigation bar:
   * 1. If an open (active or upcoming) competition exists, returns { isVisible: true, isOpen: true }.
   * 2. If NO open competition exists:
   *    - The contest remains visible for 2 days (48 hours) after the most recent competition
   *      ended/closed so community members can review the final entries, leaderboard, and winners.
   *    - After 2 days (48 hours) have passed since closing, the contest becomes completely invisible on the nav bar.
   * 3. If no competitions exist at all, returns { isVisible: false, isOpen: false }.
   */
  async getCompetitionNavStatus(): Promise<{ isVisible: boolean; isOpen: boolean }> {
    try {
      const allComps = await this.getAllCompetitions();
      if (!allComps || allComps.length === 0) {
        return { isVisible: false, isOpen: false };
      }

      const now = Date.now();
      const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000; // 48 hours in milliseconds

      // Check for any currently open or upcoming competition
      const openComp = allComps.find((comp) => {
        // Explicitly closed or completed are not open
        if (comp.status === 'completed' || comp.status === 'ended' || comp.status === 'closed') {
          return false;
        }

        if (comp.status === 'active' || comp.status === 'upcoming') {
          // If end_date is specified, check if it hasn't passed
          if (comp.end_date) {
            const endMs = new Date(comp.end_date).getTime();
            if (!isNaN(endMs) && endMs > now) {
              return true;
            }
            // If end_date passed, but announcement_date is still in the future
            if (comp.announcement_date) {
              const annMs = new Date(comp.announcement_date).getTime();
              if (!isNaN(annMs) && annMs > now) {
                return true;
              }
            }
            return false;
          }
          return true;
        }

        // Even if status string is empty, check if dates indicate active
        if (comp.end_date) {
          const endMs = new Date(comp.end_date).getTime();
          if (!isNaN(endMs) && endMs > now) {
            return true;
          }
        }

        return false;
      });

      if (openComp) {
        const now = Date.now();
        const startMs = openComp.start_date ? new Date(openComp.start_date).getTime() : 0;
        const endMs = openComp.end_date ? new Date(openComp.end_date).getTime() : Infinity;
        const isTimeActive = now >= startMs && now <= endMs;
        
        return { 
          isVisible: true, 
          isOpen: openComp.status === 'active' || isTimeActive 
        };
      }

      // No open competition exists.
      // Check when the most recent competition concluded.
      let mostRecentClosedTime = 0;
      for (const comp of allComps) {
        const dateCandidates = [
          comp.end_date,
          comp.close_date,
          comp.announcement_date,
          comp.created_at,
        ]
          .filter(Boolean)
          .map((d) => new Date(d!).getTime())
          .filter((t) => !isNaN(t));

        if (dateCandidates.length > 0) {
          const compLatest = Math.max(...dateCandidates);
          if (compLatest > mostRecentClosedTime) {
            mostRecentClosedTime = compLatest;
          }
        }
      }

      if (mostRecentClosedTime > 0) {
        const timeSinceClosed = now - mostRecentClosedTime;
        // If within 2 days (48 hours) after concluding, remain visible for leaderboard review
        if (timeSinceClosed <= TWO_DAYS_MS) {
          return { isVisible: true, isOpen: false };
        }
      }

      // More than two days have passed since the last competition concluded
      // The contest becomes invisible on the nav bar.
      return { isVisible: false, isOpen: false };
    } catch (e) {
      console.warn("getCompetitionNavStatus check error:", e);
      return { isVisible: false, isOpen: false };
    }
  },

  async hasAnyCompetitions(): Promise<boolean> {
    try {
      const status = await this.getCompetitionNavStatus();
      return status.isVisible;
    } catch {
      return false;
    }
  },

  async joinCompetition(
    competitionId: string,
    resourceId: string,
    type: "post" | "book" | "essay" = "post",
  ) {
    const user = await getCurrentUserInternal();
    if (!user) throw new Error("Not logged in");

    const comp = await this.getCompetitionById(competitionId);
    const initialStageId = comp?.stages?.[0]?.id || 'stage-1';
    const entry = {
      competition_id: competitionId,
      user_id: user.id,
      post_id: type === "book" ? null : resourceId,
      book_id: type === "book" ? resourceId : null,
      stage_status: 'submitted',
      current_stage_id: initialStageId,
    };

    let savedRemotely = false;
    let apiError: Error | null = null;
    try {
      const { data: sessionData } = await supabase?.auth.getSession() || { data: null };
      const token = sessionData?.session?.access_token;
      const response = await fetch('/api/competition-entries', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: 'Bearer ' + token } : {}),
        },
        body: JSON.stringify({
          competition_id: competitionId,
          work_id: resourceId,
          type,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok) {
        savedRemotely = true;
      } else if (response.status < 500) {
        throw new Error(result.error || 'Your entry could not be submitted.');
      } else {
        apiError = new Error(result.error || `Entry service returned ${response.status}.`);
        console.warn('Competition entry API failed; trying direct Supabase:', apiError);
      }
    } catch (err) {
      if (err instanceof Error && err.name !== 'TypeError' && !err.message.startsWith('Failed to fetch')) throw err;
      apiError = err instanceof Error ? err : new Error('Entry API request failed.');
      console.warn('Competition entry API request failed; trying direct Supabase:', apiError);
    }

    if (!savedRemotely && supabase) {
      const { error } = await supabase
        .from("competition_entries")
        .insert(entry);
      if (error) {
        const baseEntry = {
          competition_id: competitionId,
          user_id: user.id,
          post_id: type === "book" ? null : resourceId,
          book_id: type === "book" ? resourceId : null,
        };
        const { error: baseErr } = await supabase
          .from("competition_entries")
          .insert(baseEntry);
        if (baseErr) {
          console.error("Competition entry insert failed:", error, baseErr);
          throw new Error(apiError?.message || baseErr.message || 'Your entry could not be submitted.');
        }
      }
      savedRemotely = true;
    }

    if (!savedRemotely) {
      throw new Error(apiError?.message || 'Your entry could not be saved to the shared database.');
    }

    if (typeof window !== 'undefined') {
      try {
        const localKey = `comp_entries_${competitionId}`;
        const existing = JSON.parse(localStorage.getItem(localKey) || '[]');
        existing.unshift({
          id: `entry-${Date.now()}`,
          competition_id: competitionId,
          user_id: user.id,
          post_id: type === "book" ? null : resourceId,
          book_id: type === "book" ? resourceId : null,
          entry_date: new Date().toISOString(),
          ink_drop_votes: 0,
          stage_status: 'submitted',
          current_stage_id: initialStageId,
        });
        localStorage.setItem(localKey, JSON.stringify(existing));
      } catch (error) {
        console.warn('Could not update the local competition entry cache:', error);
      }
    }
  },

  async advanceCompetitionEntry(
    competitionId: string,
    entryId: string,
    stageId: string,
    stageStatus: string = 'advanced'
  ): Promise<void> {
    const comp = await this.getCompetitionById(competitionId);
    if (comp) {
      const stages = comp.stages || [];
      const stage = stages.find(s => s.id === stageId);
      if (stage) {
        if (!stage.advanced_entry_ids) stage.advanced_entry_ids = [];
        if (!stage.advanced_entry_ids.includes(entryId)) {
          stage.advanced_entry_ids.push(entryId);
        }
      }
      await this.updateCompetition(competitionId, { stages });
    }

    // Persist entry status locally
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(`comp_entry_stage_${entryId}`, JSON.stringify({
          stage_id: stageId,
          stage_status: stageStatus,
          updated_at: new Date().toISOString()
        }));
      } catch {}
    }

    if (supabase) {
      try {
        await supabase
          .from("competition_entries")
          .update({ stage_status: stageStatus, current_stage_id: stageId })
          .eq("id", entryId);
      } catch (e) {
        console.warn("DB update stage_status error:", e);
      }
    }
  },

  async advanceMultipleCompetitionEntries(
    competitionId: string,
    entryIds: string[],
    stageId: string,
    stageStatus: string = 'advanced'
  ): Promise<void> {
    const comp = await this.getCompetitionById(competitionId);
    if (comp) {
      const stages = comp.stages || [];
      const stage = stages.find(s => s.id === stageId);
      if (stage) {
        if (!stage.advanced_entry_ids) stage.advanced_entry_ids = [];
        for (const eid of entryIds) {
          if (!stage.advanced_entry_ids.includes(eid)) {
            stage.advanced_entry_ids.push(eid);
          }
        }
      }
      await this.updateCompetition(competitionId, { stages });
    }

    for (const eid of entryIds) {
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(`comp_entry_stage_${eid}`, JSON.stringify({
            stage_id: stageId,
            stage_status: stageStatus,
            updated_at: new Date().toISOString()
          }));
        } catch {}
      }
      if (supabase) {
        try {
          await supabase
            .from("competition_entries")
            .update({ stage_status: stageStatus, current_stage_id: stageId })
            .eq("id", eid);
        } catch (e) {}
      }
    }
  },

  async getCompetitionLeaderboard(
    competitionId: string,
  ): Promise<CompetitionEntry[]> {
    let entries: CompetitionEntry[] = [];
    if (supabase) {
      try {
        const { data } = await supabase
          .from("competition_entries")
          .select(
            "*, profiles(avatar_url, display_name, full_name), posts(title, content, background_url, type)",
          )
          .eq("competition_id", competitionId)
          .order("score", { ascending: false });

        if (data && data.length) {
          entries = data.map((entry: any) => ({
            ...entry,
            author_name: entry.profiles
              ? formatAuthorName(
                  entry.profiles.full_name,
                  entry.profiles.display_name,
                )
              : "Anonymous",
            author_avatar: entry.profiles?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${entry.user_id}`,
            post_title: entry.posts?.title || "Competition Submission",
            post_type: entry.posts?.type,
            post_content_snippet: entry.posts?.content?.substring(0, 100) || "",
            post_background_url: entry.posts?.background_url,
          }));
        }
      } catch (e) {
        console.warn("getCompetitionLeaderboard DB query error:", e);
      }
    }

    // Merge any locally tracked entries or stage statuses
    if (typeof window !== 'undefined') {
      try {
        const localEntries: any[] = JSON.parse(localStorage.getItem(`comp_entries_${competitionId}`) || '[]');
        for (const le of localEntries) {
          if (!entries.some(e => e.id === le.id || (e.post_id && e.post_id === le.post_id))) {
            entries.push(le);
          }
        }
        // Enrich with local stage status if set
        entries = entries.map(e => {
          try {
            const raw = localStorage.getItem(`comp_entry_stage_${e.id}`);
            if (raw) {
              const meta = JSON.parse(raw);
              return { ...e, stage_status: meta.stage_status, current_stage_id: meta.stage_id };
            }
          } catch {}
          return e;
        });
      } catch {}
    }

    return entries;
  },

  async getGlobalLeaderboard(
    sortBy: "total_views" | "total_likes" | "followers_count" = "total_views",
    limit: number = 50,
  ): Promise<Profile[]> {
    if (supabase) {
      const { data } = await supabase
        .from("profiles")
        .select(STABLE_PROFILE_COLUMNS)
        .order(sortBy, { ascending: false })
        .limit(limit);

      return (data || []).map((p: any) => this.processProfileResult(p));
    }
    return [];
  },

  async getMyPayouts(): Promise<PayoutTransaction[]> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) return [];
      const { data } = await supabase
        .from("payout_transactions")
        .select("*")
        .eq("user_id", user.id)
        .eq("status", "processed")
        .order("date", { ascending: false });
      return data || [];
    }
    return [];
  },
  async getAllPayouts(): Promise<PayoutTransaction[]> {
    if (supabase) {
      const { data } = await supabase
        .from("payout_transactions")
        .select("*")
        .order("date", { ascending: false });
      return data || [];
    }
    return [];
  },
  async requestPayout(amount: number, method: string, bankDetails: string) {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) throw new Error("Not authenticated");

      const profile = await this.getProfile();
      if (!profile) throw new Error("Profile not found");
      
      const { error } = await supabase.from("payout_transactions").insert({
        user_id: user.id,
        user_name: profile.display_name,
        amount,
        method,
        bank_details_snapshot: bankDetails,
        status: "pending",
        reference: `PAY-${Date.now()}`,
      });

      if (error) throw error;
      
      // Deduct the requested payout by clearing out the read metrics
      await supabase
        .from("profiles")
        .update({ 
            monthly_read_time: 0,
            ink_drop_balance: 0,
            book_reads: 0,
            poem_views: 0,
            total_earnings: (profile.total_earnings || 0) + amount
        })
        .eq("id", user.id);
    }
  },
  async getAllUsers(): Promise<Profile[]> {
    let resultList: Profile[] = [];
    const seenIds = new Set<string>();

    if (supabase) {
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .order("created_at", { ascending: false });

        if (!error && data && data.length > 0) {
          data.forEach((p: any) => {
            const processed = this.processProfileResult(p);
            if (processed && processed.id) {
              resultList.push(processed);
              seenIds.add(processed.id);
            }
          });
        } else if (error) {
          console.warn("getAllUsers profiles select failed:", error);
          const { data: fallbackData } = await supabase
            .from("profiles")
            .select(MINIMAL_PROFILE_COLUMNS);
          if (fallbackData) {
            fallbackData.forEach((p: any) => {
              const processed = this.processProfileResult(p);
              if (processed && processed.id && !seenIds.has(processed.id)) {
                resultList.push(processed);
                seenIds.add(processed.id);
              }
            });
          }
        }
      } catch (err) {
        console.warn("getAllUsers database fetch failed:", err);
      }

      // Multi-table author & user discovery across all platform tables
      try {
        const discoveredUserMap = new Map<string, { name?: string; avatar?: string; email?: string }>();

        const [postsRes, booksRes, commentsRes, likesRes, followsRes, payoutsRes, notifsRes, bookmarksRes, membersRes] = await Promise.all([
          supabase.from("posts").select("user_id, author_name, author_avatar").limit(300),
          supabase.from("books").select("author_id, user_id, author_name, author_avatar").limit(300),
          supabase.from("comments").select("user_id, author_name, author_avatar").limit(300),
          supabase.from("likes").select("user_id").limit(300),
          supabase.from("follows").select("follower_id, following_id").limit(300),
          supabase.from("payout_transactions").select("user_id, user_name").limit(300),
          supabase.from("notifications").select("user_id, actor_id").limit(300),
          supabase.from("bookmarks").select("user_id").limit(300),
          supabase.from("community_members").select("user_id").limit(300)
        ]);

        (postsRes.data || []).forEach((p: any) => {
          if (p.user_id && !seenIds.has(p.user_id)) {
            discoveredUserMap.set(p.user_id, { name: cleanAuthorName(p.author_name), avatar: p.author_avatar });
          }
        });

        (booksRes.data || []).forEach((b: any) => {
          const id = b.author_id || b.user_id;
          if (id && !seenIds.has(id)) {
            discoveredUserMap.set(id, { name: cleanAuthorName(b.author_name), avatar: b.author_avatar });
          }
        });

        (commentsRes.data || []).forEach((c: any) => {
          if (c.user_id && !seenIds.has(c.user_id)) {
            discoveredUserMap.set(c.user_id, { name: cleanAuthorName(c.author_name), avatar: c.author_avatar });
          }
        });

        (likesRes.data || []).forEach((l: any) => {
          if (l.user_id && !seenIds.has(l.user_id) && !discoveredUserMap.has(l.user_id)) {
            discoveredUserMap.set(l.user_id, {});
          }
        });

        (followsRes.data || []).forEach((f: any) => {
          if (f.follower_id && !seenIds.has(f.follower_id) && !discoveredUserMap.has(f.follower_id)) {
            discoveredUserMap.set(f.follower_id, {});
          }
          if (f.following_id && !seenIds.has(f.following_id) && !discoveredUserMap.has(f.following_id)) {
            discoveredUserMap.set(f.following_id, {});
          }
        });

        (payoutsRes.data || []).forEach((p: any) => {
          if (p.user_id && !seenIds.has(p.user_id)) {
            discoveredUserMap.set(p.user_id, { name: p.user_name });
          }
        });

        (notifsRes.data || []).forEach((n: any) => {
          if (n.user_id && !seenIds.has(n.user_id) && !discoveredUserMap.has(n.user_id)) {
            discoveredUserMap.set(n.user_id, {});
          }
          if (n.actor_id && !seenIds.has(n.actor_id) && !discoveredUserMap.has(n.actor_id)) {
            discoveredUserMap.set(n.actor_id, {});
          }
        });

        (bookmarksRes.data || []).forEach((b: any) => {
          if (b.user_id && !seenIds.has(b.user_id) && !discoveredUserMap.has(b.user_id)) {
            discoveredUserMap.set(b.user_id, {});
          }
        });

        (membersRes.data || []).forEach((m: any) => {
          if (m.user_id && !seenIds.has(m.user_id) && !discoveredUserMap.has(m.user_id)) {
            discoveredUserMap.set(m.user_id, {});
          }
        });

        // Query missing profiles directly from database by ID list
        const missingIds = Array.from(discoveredUserMap.keys());
        if (missingIds.length > 0) {
          try {
            const { data: fetchedProfiles } = await supabase
              .from("profiles")
              .select("*")
              .in("id", missingIds);

            if (fetchedProfiles && fetchedProfiles.length > 0) {
              fetchedProfiles.forEach((fp: any) => {
                const processed = this.processProfileResult(fp);
                if (processed && processed.id && !seenIds.has(processed.id)) {
                  seenIds.add(processed.id);
                  resultList.push(processed);
                  discoveredUserMap.delete(processed.id);
                }
              });
            }
          } catch (e) {
            console.warn("getAllUsers .in('id') profile query warning:", e);
          }

          // For any remaining discovered user IDs, construct a profile
          discoveredUserMap.forEach((meta, id) => {
            if (!seenIds.has(id)) {
              seenIds.add(id);
              const displayName = meta.name || `User_${id.substring(0, 6)}`;
              resultList.push({
                id: id,
                display_name: displayName,
                full_name: displayName,
                bio: "Literally Community Member",
                avatar_url: meta.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${id}`,
                email: meta.email || `${id.substring(0, 8)}@literally.app`,
                joined_date: new Date().toISOString(),
                created_at: new Date().toISOString(),
                has_selected_pen_name: true,
                wallet_balance: 0,
                ink_drop_balance: 0,
                total_likes: 0,
                followers_count: 0,
                following_count: 0,
                reading_streak: 0,
                is_admin: false,
                is_active: true,
                show_nsfw: false,
                is_adult: false,
                is_verified: false,
              } as unknown as Profile);
            }
          });
        }
      } catch (discErr) {
        console.warn("getAllUsers discovery query warning:", discErr);
      }
    }

    // Add current logged in user's profile
    try {
      const currentProfile = await this.getProfile();
      if (currentProfile && !seenIds.has(currentProfile.id)) {
        seenIds.add(currentProfile.id);
        resultList.push(currentProfile);
      }
    } catch (e) {
      console.warn("getAllUsers getProfile check failed:", e);
    }

    // Combine with resolvedProfileCache
    Object.values(resolvedProfileCache).forEach((p) => {
      if (p && p.id && !seenIds.has(p.id)) {
        seenIds.add(p.id);
        resultList.push(p);
      }
    });

    // Combine with local cached users
    try {
      const rawLocal = localStorage.getItem("literally_all_cached_users");
      if (rawLocal) {
        const parsed: Profile[] = JSON.parse(rawLocal);
        parsed.forEach((p) => {
          if (p && p.id && !seenIds.has(p.id)) {
            seenIds.add(p.id);
            resultList.push(p);
          }
        });
      }
      localStorage.setItem("literally_all_cached_users", JSON.stringify(resultList));
    } catch (e) {
      console.warn("getAllUsers localStorage caching error:", e);
    }

    return resultList;
  },
  async giveInkToBook(bookId: string, amount: number) {
    if (supabase) {
      // In a real app, use a stored procedure or transaction
      const user = await getCurrentUserInternal();
      if (!user) return;

      // Deduct from user
      await supabase.rpc("deduct_ink", { amount, user_id: user.id });
      // Add to book
      await supabase.rpc("add_ink_to_book", { amount, book_id: bookId });

      // Notify author
      const { data: book } = await supabase
        .from("books")
        .select("author_id, title")
        .eq("id", bookId)
        .maybeSingle();
      if (book && book.author_id !== user.id) {
        await supabase.from("notifications").insert({
          user_id: book.author_id,
          actor_id: user.id,
          type: "tip",
          resource_id: bookId,
          resource_type: "book",
          preview_text: `Gifted ${amount} Ink to "${book.title}"`,
          is_read: false,
        });
      }
    } else {
      throw new Error("Supabase not configured.");
    }
  },

  async sendInk(postId: string, amount: number = 1): Promise<void> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) throw new Error("Not logged in");

      try {
        await supabase.rpc("deduct_ink", { amount, user_id: user.id });
      } catch (e) {
        console.warn("deduct_ink RPC warning:", e);
      }

      try {
        const { data: post } = await supabase
          .from("posts")
          .select("author_id, title")
          .eq("id", postId)
          .maybeSingle();

        if (post && post.author_id !== user.id) {
          await supabase.from("notifications").insert({
            user_id: post.author_id,
            actor_id: user.id,
            type: "tip",
            resource_id: postId,
            resource_type: "post",
            preview_text: `Supported "${post.title || 'your work'}" with ${amount} Ink Drop(s)`,
            is_read: false,
          });
        }
      } catch (e) {
        console.warn("sendInk notification warning:", e);
      }
    }
  },

  async getNotifications(): Promise<Notification[]> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) return [];

      const { data } = await supabase
        .from("notifications")
        .select("*, actor:profiles!actor_id(display_name, avatar_url)")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);

      return (data || []).map((n: any) => ({
        ...n,
        actor_name: n.actor?.display_name,
        actor_avatar: n.actor?.avatar_url,
      }));
    }
    return [];
  },

  async markNotificationsAsRead(): Promise<void> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) return;

      await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("user_id", user.id)
        .eq("is_read", false);
    }
  },

  async submitToCompetition(
    competitionId: string,
    resourceId: string,
    resourceType: "poem" | "article" | "essay" | "book",
  ): Promise<void> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) throw new Error("Not authenticated");

      const entry: any = {
        competition_id: competitionId,
        user_id: user.id,
      };

      if (
        resourceType === "poem" ||
        resourceType === "article" ||
        resourceType === "essay"
      )
        entry.post_id = resourceId;
      else entry.book_id = resourceId;

      const { error } = await supabase
        .from("competition_entries")
        .insert(entry);
      if (error) {
        if (error.code === "23505")
          throw new Error("You have already entered this competition.");
        throw error;
      }

      // Increment entry count
      await supabase.rpc("increment_competition_entries", {
        comp_id: competitionId,
      });
    }
  },

  async getRecommendedFeed(
    type?: "poem" | "article" | "essay" | "reads",
    limit: number = 100,
    offset: number = 0,
  ): Promise<Post[]> {
    let dbPosts: Post[] = [];
    if (supabase) {
      try {
        let query = supabase.from("posts").select("*, profiles(avatar_url, display_name)");
        if (type === "reads") {
          query = query.in("type", ["article", "essay"]);
        } else if (type === "poem" || !type) {
          query = query.or("type.eq.poem,type.eq.Poem,type.is.null");
        } else {
          query = query.eq("type", type);
        }

        const { data, error } = await query
          .order("created_at", { ascending: false })
          .range(offset, offset + limit - 1);
        
        if (!error && data && data.length > 0) {
          dbPosts = data.map(mapPostWithProfile);
        } else {
          // Fallback simple query without join
          let simpleQuery = supabase.from("posts").select("*");
          if (type === "reads") {
            simpleQuery = simpleQuery.in("type", ["article", "essay"]);
          } else if (type === "poem" || !type) {
            simpleQuery = simpleQuery.or("type.eq.poem,type.eq.Poem,type.is.null");
          } else {
            simpleQuery = simpleQuery.eq("type", type);
          }
          const { data: simpleData } = await simpleQuery.order("created_at", { ascending: false }).range(offset, offset + limit - 1);
          if (simpleData && simpleData.length > 0) {
            dbPosts = simpleData.map(mapPostWithProfile);
          }
        }
      } catch (e) {
        console.warn("getRecommendedFeed DB query fallback:", e);
      }
    }

    const serverPosts = await fetchServerPosts({ type, limit, offset });
    const localPosts = this.getLocalPosts().filter(p => {
      if (type === "reads") return p.type === "article" || p.type === "essay";
      if (type === "poem" || !type) return !p.type || p.type.toLowerCase() === "poem";
      return p.type === type;
    });

    const combined = [...localPosts, ...serverPosts, ...dbPosts].map(p => ({
      ...p,
      author_name: cleanAuthorName(p.author_name)
    }));

    const deduplicated = Array.from(new Map(combined.map((p) => [p.id, p])).values());

    if (type === "reads" || type === "article" || type === "essay") {
      return this.rankArticlesForUser(deduplicated, "guest");
    }
    return this.shufflePoemsForUser(deduplicated, "guest");
  },

  async trackRead(resourceId: string, resourceType: "post" | "book") {
    const realId = getRealId(resourceId);
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) return;

      const entry: any = { user_id: user.id };
      if (resourceType === "post") entry.post_id = realId;
      else entry.book_id = realId;

      try {
        // 1. Insert or update reading history without creating duplicate rows
        let historyQuery = supabase
          .from("reading_history")
          .select("id")
          .eq("user_id", user.id);
        if (resourceType === "post") {
          historyQuery = historyQuery.eq("post_id", realId);
        } else {
          historyQuery = historyQuery.eq("book_id", realId);
        }
        const { data: existingEntry } = await historyQuery.limit(1).maybeSingle();
        if (existingEntry) {
          await supabase
            .from("reading_history")
            .update({ last_read_at: new Date().toISOString() })
            .eq("id", existingEntry.id);
        } else {
          await supabase.from("reading_history").insert(entry);
        }

        // 2. Update streak
        const { data: profile } = await supabase
          .from("profiles")
          .select("reading_streak, last_read_at, longest_streak")
          .eq("id", user.id)
          .maybeSingle();

        if (profile) {
          const now = new Date();
          const lastRead = profile.last_read_at
            ? new Date(profile.last_read_at)
            : null;
          let newStreak = profile.reading_streak || 0;

          const isToday =
            lastRead &&
            lastRead.getDate() === now.getDate() &&
            lastRead.getMonth() === now.getMonth() &&
            lastRead.getFullYear() === now.getFullYear();

          const isYesterday =
            lastRead &&
            (() => {
              const yesterday = new Date(now);
              yesterday.setDate(now.getDate() - 1);
              return (
                lastRead.getDate() === yesterday.getDate() &&
                lastRead.getMonth() === yesterday.getMonth() &&
                lastRead.getFullYear() === yesterday.getFullYear()
              );
            })();

          if (!isToday) {
            if (isYesterday || !lastRead) {
              newStreak += 1;
            } else {
              newStreak = 1; // Reset streak
            }

            const updates: any = {
              reading_streak: newStreak,
              last_read_at: now.toISOString(),
            };

            if (newStreak > (profile.longest_streak || 0)) {
              updates.longest_streak = newStreak;
            }

            await supabase.from("profiles").update(updates).eq("id", user.id);

            // Notify UI of update
            const updatedProfile = { ...profile, ...updates };
            window.dispatchEvent(
              new CustomEvent("profileUpdated", { detail: updatedProfile }),
            );
          }
        }

        // If this was a post read, also automatically update user article taste preferences
        if (resourceType === "post" && realId) {
          const { data: postData } = await supabase.from("posts").select("id, type, themes, title").eq("id", realId).maybeSingle();
          if (postData) {
            this.trackArticleTaste(user.id, postData);
          }
        }
      } catch (err) {
        console.warn("trackRead failed (likely missing columns or RLS):", err);
      }
    }
  },

  /**
   * Track user article reading taste (types, themes, niches)
   */
  trackArticleTaste(userId: string, article: { id?: string; type?: string; themes?: string[]; title?: string }) {
    if (!article) return;
    try {
      const uid = userId || 'guest';
      const key = `literally_article_taste_${uid}`;
      const raw = localStorage.getItem(key);
      const taste = raw ? JSON.parse(raw) : { types: {}, themes: {}, readIds: [] };
      taste.types = taste.types || {};
      taste.themes = taste.themes || {};
      taste.readIds = taste.readIds || [];

      if (article.type) {
        taste.types[article.type] = (taste.types[article.type] || 0) + 1;
      }
      if (Array.isArray(article.themes)) {
        article.themes.forEach((t: string) => {
          if (t && typeof t === 'string') {
            const cleanT = t.trim();
            if (cleanT) {
              taste.themes[cleanT] = (taste.themes[cleanT] || 0) + 1;
            }
          }
        });
      }
      if (article.id && !taste.readIds.includes(article.id)) {
        taste.readIds.push(article.id);
        if (taste.readIds.length > 200) taste.readIds.shift();
      }

      localStorage.setItem(key, JSON.stringify(taste));
    } catch (e) {
      console.warn("Could not record article taste:", e);
    }
  },

  /**
   * Retrieve user article reading taste
   */
  getUserArticleTaste(userId?: string): { types: Record<string, number>; themes: Record<string, number>; readIds: string[] } {
    try {
      const uid = userId || 'guest';
      const raw = localStorage.getItem(`literally_article_taste_${uid}`);
      if (raw) return JSON.parse(raw);
    } catch {}
    return { types: {}, themes: {}, readIds: [] };
  },

  /**
   * Shuffles poem feed deterministically per user session so old poems are resurfaced and viewed
   * without duplicating or skipping items across pagination updates.
   */
  shufflePoemsForUser(posts: Post[], userId: string = 'guest'): Post[] {
    if (!posts || posts.length <= 1) return posts;

    // Deduplicate incoming posts by id first
    const uniquePosts = Array.from(new Map(posts.map((p) => [p.id, p])).values());

    // Deterministic daily/session seed per user so order remains fixed during scrolling
    const todayStr = new Date().toISOString().split('T')[0];
    const seedString = `${userId}_${todayStr}`;
    let seedHash = 0;
    for (let i = 0; i < seedString.length; i++) {
      seedHash = (seedHash << 5) - seedHash + seedString.charCodeAt(i);
      seedHash |= 0;
    }
    seedHash = Math.abs(seedHash) || 12345;

    const pseudoRandom = (step: number) => {
      const x = Math.sin(seedHash + step * 100) * 10000;
      return x - Math.floor(x);
    };

    const now = Date.now();
    const THREE_DAYS = 3 * 24 * 60 * 60 * 1000;

    const recent: Post[] = [];
    const older: Post[] = [];

    uniquePosts.forEach((p) => {
      const created = new Date(p.created_at || 0).getTime();
      if (now - created < THREE_DAYS) {
        recent.push(p);
      } else {
        older.push(p);
      }
    });

    const deterministicShuffle = <T>(arr: T[], offsetSeed: number): T[] => {
      const copy = [...arr];
      for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(pseudoRandom(offsetSeed + i) * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
      }
      return copy;
    };

    const shuffledRecent = deterministicShuffle(recent, 100);
    const shuffledOlder = deterministicShuffle(older, 500);

    if (shuffledOlder.length === 0) return shuffledRecent;
    if (shuffledRecent.length === 0) return shuffledOlder;

    const result: Post[] = [];
    let rIdx = 0;
    let oIdx = 0;

    // Interleave recent and older catalog works deterministically
    while (rIdx < shuffledRecent.length || oIdx < shuffledOlder.length) {
      if (rIdx < shuffledRecent.length) result.push(shuffledRecent[rIdx++]);
      if (oIdx < shuffledOlder.length) result.push(shuffledOlder[oIdx++]);
      if (oIdx < shuffledOlder.length) result.push(shuffledOlder[oIdx++]);
      if (rIdx < shuffledRecent.length) result.push(shuffledRecent[rIdx++]);
      if (oIdx < shuffledOlder.length) result.push(shuffledOlder[oIdx++]);
    }

    return Array.from(new Map(result.map((p) => [p.id, p])).values());
  },


  /**
   * Adjusts and ranks articles based on the types and themes of articles read by the user
   */
  rankArticlesForUser(articles: Post[], userId: string = 'guest'): Post[] {
    if (!articles || articles.length <= 1) return articles;

    const taste = this.getUserArticleTaste(userId);
    const hasTasteData = Object.keys(taste.themes).length > 0 || Object.keys(taste.types).length > 0;

    if (!hasTasteData) {
      // Return standard recency sort if user hasn't read any articles yet
      return articles.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
    }

    // Determine user's top themes and preferred type
    const topThemes = Object.entries(taste.themes).sort((a, b) => b[1] - a[1]).slice(0, 5).map(e => e[0].toLowerCase());
    const preferredType = taste.types['essay'] > (taste.types['article'] || 0) ? 'essay' : 'article';

    const scored = articles.map((art) => {
      let score = 0;

      // 1. Theme affinity boost (+25 pts per matched theme)
      if (Array.isArray(art.themes)) {
        art.themes.forEach((t) => {
          if (typeof t === 'string') {
            const clean = t.trim().toLowerCase();
            if (topThemes.includes(clean)) {
              const freq = taste.themes[clean] || taste.themes[t] || 1;
              score += Math.min(freq * 15, 60);
            }
          }
        });
      }

      // Also check content/title for theme mentions
      topThemes.forEach((th) => {
        if ((art.title || '').toLowerCase().includes(th)) score += 20;
        if (((art as any).post_content_snippet || art.content || '').toLowerCase().includes(th)) score += 10;
      });

      // 2. Format / Type affinity (+20 pts if matching user's preferred format)
      if (art.type === preferredType) {
        score += 20;
      }

      // 3. Social proof / engagement score
      score += Math.min((art.likes_count || 0) * 3, 30);
      score += Math.min((art.views_count || 0) * 0.2, 15);

      // 4. Freshness bonus (decay factor)
      const ageHours = (Date.now() - new Date(art.created_at || 0).getTime()) / (1000 * 60 * 60);
      const freshness = Math.max(0, 40 - (ageHours / 24) * 2);
      score += freshness;

      return { post: art, score };
    });

    // Sort by personalized score descending
    scored.sort((a, b) => b.score - a.score);
    return scored.map(s => s.post);
  },


  
  async getFollowingFeed(
    type: "poem" | "essay" | "article" = "poem",
    limit = 50,
    offset = 0
  ): Promise<Post[]> {
    if (!supabase) return [];
    try {
      const user = await getCurrentUserInternal();
      if (!user) return [];

      const { data: followsData, error: followsError } = await supabase
        .from("follows")
        .select("following_id")
        .eq("follower_id", user.id);

      if (followsError) throw followsError;

      const followedIds = (followsData || []).map((f: any) => f.following_id);
      if (followedIds.length === 0) return [];

      let query = supabase
        .from("posts")
        .select("*, profiles!fk_posts_profiles(avatar_url, display_name)")
        .in("user_id", followedIds)
        .eq("status", "published");

      if (type === "poem") {
        query = query.or("type.eq.poem,type.eq.Poem,type.is.null");
      } else {
        query = query.eq("type", type);
      }

      const { data, error } = await query
        .order("created_at", { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) throw error;
      return (data || []).map(mapPostWithProfile);
    } catch (e) {
      console.error("getFollowingFeed error:", e);
      return [];
    }
  },

  async getPersonalizedFeed(
    type?: "poem" | "article" | "essay" | "reads",
    limit: number = 100,
    offset: number = 0,
  ): Promise<Post[]> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) return this.getRecommendedFeed(type, limit, offset);

      try {
        const [followsResult, likesResult, historyResult, recommended, ownPostsResult] = await Promise.all([
          supabase.from("follows").select("following_id").eq("follower_id", user.id),
          supabase.from("likes").select("book_id, books(genre)").eq("user_id", user.id).not("book_id", "is", null),
          supabase.from("reading_history").select("post_id").eq("user_id", user.id).not("post_id", "is", null),
          this.getRecommendedFeed(type, limit, offset),
          supabase.from("posts").select("*, profiles!fk_posts_profiles(avatar_url, display_name)").eq("user_id", user.id).eq("status", "published")
        ]);

        const followedIds = (followsResult.data || []).map((f: any) => f.following_id);
        const genres = (likesResult.data || [])
          .map((lb: any) => lb.books?.genre)
          .filter(Boolean);
        const topGenres = [...new Set(genres)];
        const localPosts = this.getLocalPosts();
        
        const dbOwnPosts = (ownPostsResult?.data || []).map(mapPostWithProfile);
        const localOwnPosts = localPosts.filter(p => p.user_id === user.id);
        const ownPosts = Array.from(new Map([...localOwnPosts, ...dbOwnPosts].map(p => [p.id, p])).values());

        // Filter own posts by requested type
        const filteredOwnPosts = ownPosts.filter((p: any) => {
          if (type === "reads") return p.type === "article" || p.type === "essay";
          if (type === "poem" || !type) return !p.type || p.type.toLowerCase() === "poem";
          return p.type === type;
        }).sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        // Shortcut: If they follow no one and have no liked genres, return own posts + recommended.
        if (followedIds.length === 0 && topGenres.length === 0) {
          const recommendedFiltered = recommended.filter((p) => p.user_id !== user.id);
          const final = [...filteredOwnPosts, ...recommendedFiltered];
          let deduplicated = Array.from(new Map(final.map((p) => [p.id, p])).values());
          if (type === "reads" || type === "article" || type === "essay") {
            return this.rankArticlesForUser(deduplicated, user.id);
          }
          return this.shufflePoemsForUser(deduplicated, user.id);
        }

        // Parallelize fetching from followed authors and overlapping themes
        const queryPromises: any[] = [];

        if (followedIds.length > 0) {
          let query = supabase
            .from("posts")
            .select("*, profiles!fk_posts_profiles(avatar_url, display_name)")
            .in("user_id", followedIds)
            .eq("status", "published");

          if (type === "reads") {
            query = query.in("type", ["article", "essay"]);
          } else if (type === "poem" || !type) {
            query = query.or("type.eq.poem,type.eq.Poem,type.is.null");
          } else {
            query = query.eq("type", type);
          }

          queryPromises.push(query.order("created_at", { ascending: false }).range(offset, offset + limit - 1));
        } else {
          queryPromises.push(Promise.resolve({ data: [] }));
        }

        if (topGenres.length > 0) {
          let query = supabase
            .from("posts")
            .select("*, profiles!fk_posts_profiles(avatar_url, display_name)")
            .overlaps("themes", topGenres)
            .eq("status", "published");

          if (type === "reads") {
            query = query.in("type", ["article", "essay"]);
          } else if (type === "poem" || !type) {
            query = query.or("type.eq.poem,type.eq.Poem,type.is.null");
          } else {
            query = query.eq("type", type);
          }

          queryPromises.push(query.order("likes_count", { ascending: false }).range(offset, offset + limit - 1));
        } else {
          queryPromises.push(Promise.resolve({ data: [] }));
        }

        const [followedRes, genreRes] = await Promise.all(queryPromises);
        const followedPosts = (followedRes.data || []).map(mapPostWithProfile);
        const genrePosts = (genreRes.data || []).map(mapPostWithProfile);

        // Return own posts + followed posts + genre posts + recommended posts
        const allCombined = [...filteredOwnPosts, ...followedPosts, ...genrePosts, ...recommended];
        const deduplicated = Array.from(new Map(allCombined.map((p) => [p.id, p])).values());
        
        if (type === "reads" || type === "article" || type === "essay") {
          return this.rankArticlesForUser(deduplicated, user.id);
        }
        return this.shufflePoemsForUser(deduplicated, user.id);
      } catch (err) {
        console.warn("Optimized getPersonalizedFeed failed, falling back to basic recommendations", err);
        return this.getRecommendedFeed(type, limit, offset);
      }
    }
    return [];
  },

  async incrementPoemViews(authorId: string): Promise<void> {
    if (!authorId) return;
    try {
      const user = await getCurrentUserInternal().catch(() => null);
      if (user?.id === authorId) return; // Prevent author self-view inflation

      if (isAuthorViewedLocally(authorId)) return;
      markAuthorViewedLocally(authorId);

      if (supabase) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("total_views, poem_views")
          .eq("id", authorId)
          .maybeSingle();
        if (profile) {
          const { error } = await supabase
            .from("profiles")
            .update({
              total_views: (profile.total_views || 0) + 1,
              poem_views: (profile.poem_views || 0) + 1
            })
            .eq("id", authorId);
          if (error) {
            console.warn("Failed to increment views:", error);
          }
        }
      }
    } catch (err) {
      console.warn("incrementPoemViews error:", err);
    }
  },

  /**
   * Deduplicated post view counter.
   * Guarantees that once a user/browser has viewed a post,
   * revisit / refresh / reload will NEVER increment the view count again.
   * Also prevents post authors from inflating their own views.
   * Returns true if a NEW view was recorded, false if already viewed or ignored.
   */
  async incrementPostViews(postId: string, options?: { authorId?: string }): Promise<boolean> {
    const realId = (getRealId(postId) || postId || "").toLowerCase().trim();
    if (!realId) return false;

    // 1. In-memory concurrency and session deduplication
    if (_pendingViewIncrements.has(realId) || _sessionViewedPosts.has(realId)) {
      return false;
    }

    // 2. Client persistent storage deduplication (across page reloads, tab navigation, revisits)
    if (isPostViewedLocally(realId)) {
      _sessionViewedPosts.add(realId);
      return false;
    }

    _pendingViewIncrements.add(realId);

    try {
      // 3. Current user check (self-view prevention)
      const currentUser = await getCurrentUserInternal().catch(() => null);
      if (currentUser?.id && options?.authorId && currentUser.id === options.authorId) {
        _sessionViewedPosts.add(realId);
        markPostViewedLocally(realId);
        return false;
      }

      // 4. Database-level deduplication for authenticated users across all devices
      if (supabase && currentUser?.id) {
        try {
          const { data: existingRead } = await supabase
            .from("reading_history")
            .select("id")
            .eq("user_id", currentUser.id)
            .eq("post_id", realId)
            .limit(1)
            .maybeSingle();

          if (existingRead) {
            _sessionViewedPosts.add(realId);
            markPostViewedLocally(realId);
            return false;
          }
        } catch {
          // Non-blocking fallback
        }
      }

      // 5. Mark locally immediately so concurrent calls or fast refreshes don't double count
      _sessionViewedPosts.add(realId);
      markPostViewedLocally(realId);

      // 6. Record in DB reading_history for authenticated users
      if (supabase && currentUser?.id) {
        try {
          await supabase.from("reading_history").insert({
            user_id: currentUser.id,
            post_id: realId,
            last_read_at: new Date().toISOString(),
          });
        } catch {
          // Ignore duplicate constraint or RLS warning
        }
      }

      // 7. Increment views_count in Supabase database
      if (supabase) {
        try {
          const { data: post, error: fetchErr } = await supabase
            .from("posts")
            .select("views_count, user_id")
            .eq("id", realId)
            .maybeSingle();

          if (!fetchErr && post) {
            // Extra check: if current user is author, do not increment
            if (currentUser?.id && post.user_id && currentUser.id === post.user_id) {
              return false;
            }

            const currentCount = typeof post.views_count === 'number' ? post.views_count : 0;
            const newCount = currentCount + 1;
            const { error: updateErr } = await supabase
              .from("posts")
              .update({ views_count: newCount })
              .eq("id", realId);

            if (updateErr) {
              console.warn("Failed to increment views_count in DB:", updateErr);
            }
          }
        } catch (err) {
          console.warn("incrementPostViews DB error:", err);
        }
      }

      // 8. Synchronize with local server fallback
      try {
        const viewerId = currentUser?.id || getPersistentViewerId();
        await fetch(`/api/posts/${realId}/view`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ viewer_id: viewerId }),
        });
      } catch {
        // Non-fatal
      }

      return true;
    } finally {
      _pendingViewIncrements.delete(realId);
    }
  },

  async incrementBookViews(bookId: string): Promise<void> {
    const realId = getRealId(bookId);
    if (!realId) return;
    const bookKey = `book_${realId.toLowerCase().trim()}`;
    if (isPostViewedLocally(bookKey)) return;
    markPostViewedLocally(bookKey);

    if (supabase) {
      try {
        const { data: book } = await supabase
          .from("books")
          .select("total_reads")
          .eq("id", realId)
          .maybeSingle();

        if (book) {
          const { error } = await supabase
            .from("books")
            .update({
              total_reads: (book.total_reads || 0) + 1,
            })
            .eq("id", realId);
          if (error) {
            console.warn("Failed to increment book reads:", error);
          }
        }
      } catch (err) {
        console.warn("incrementBookViews error:", err);
      }
    }
  },

  async checkPenNameAvailability(name: string) {
    if (supabase) {
      try {
        const user = await getCurrentUserInternal();
        let query = supabase
          .from("profiles")
          .select("id", { count: "exact", head: true })
          .eq("display_name", name);

        if (user) {
          query = query.neq("id", user.id);
        }

        const { count, error } = await query;

        if (error) throw error;
        return count === 0;
      } catch (err) {
        console.warn("checkPenNameAvailability error:", err);
        return true;
      }
    }
    return true;
  },

  async incrementProfileView(targetId: string) {
    if (!targetId) return;
    try {
      const user = await getCurrentUserInternal().catch(() => null);
      if (user?.id === targetId) return; // Prevent self-view inflation

      if (isAuthorViewedLocally(targetId)) return;
      markAuthorViewedLocally(targetId);

      if (supabase) {
        const { error } = await supabase.rpc("increment_profile_view", { target_id: targetId });
        if (error) {
          // Fallback if RPC doesn't exist
          try {
            const { data: profile } = await supabase
              .from("profiles")
              .select("total_views")
              .eq("id", targetId)
              .maybeSingle();
            if (profile) {
              await supabase
                .from("profiles")
                .update({ total_views: (profile.total_views || 0) + 1 })
                .eq("id", targetId);
            }
          } catch (err) {
            console.warn("incrementProfileView JS fallback failed:", err);
          }
        }
      }
    } catch (err) {
      console.warn("incrementProfileView error:", err);
    }
  },

  // --- COMMUNITIES ---
  async getCommunities(type: "explore" | "my"): Promise<any[]> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) return [];

      if (type === "explore") {
        const { data } = await supabase
          .from("communities")
          .select("*")
          .order("members_count", { ascending: false });
        return data || [];
      } else {
        const { data } = await supabase
          .from("community_members")
          .select("communities(*)")
          .eq("user_id", user.id);
        return (data || []).map((d: any) => d.communities).filter(Boolean);
      }
    }
    return [];
  },

  async getCommunity(id: string): Promise<any | null> {
    if (supabase) {
      const { data } = await supabase
        .from("communities")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      return data;
    }
    return null;
  },

  async createCommunity(community: any): Promise<any> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) throw new Error("Not authenticated");

      // Check followers count (Admins bypass this)
      const { data: profile } = await supabase
        .from("profiles")
        .select("followers_count, is_admin, email")
        .eq("id", user.id)
        .maybeSingle();
      if (
        !profile?.is_admin &&
        profile?.email !== "siwonikuayomide123@gmail.com" &&
        (profile?.followers_count || 0) < 1000
      ) {
        throw new Error(
          "You need at least 1,000 followers to create a community.",
        );
      }

      const { data, error } = await supabase
        .from("communities")
        .insert({
          ...community,
          creator_id: user.id,
        })
        .select()
        .maybeSingle();

      if (error) throw error;

      // Automatically join as creator
      await supabase.from("community_members").insert({
        community_id: data.id,
        user_id: user.id,
        role: "creator",
      });

      return data;
    }
    throw new Error("Supabase not configured");
  },

  async joinCommunity(communityId: string): Promise<void> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase.from("community_members").insert({
        community_id: communityId,
        user_id: user.id,
        role: "member",
      });

      if (error) throw error;
    }
  },

  async leaveCommunity(communityId: string): Promise<void> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("community_members")
        .delete()
        .eq("community_id", communityId)
        .eq("user_id", user.id);
      if (error) throw error;
    }
  },

  async isCommunityMember(communityId: string): Promise<boolean> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) return false;

      const { data } = await supabase
        .from("community_members")
        .select("id")
        .eq("community_id", communityId)
        .eq("user_id", user.id)
        .maybeSingle();
      return !!data;
    }
    return false;
  },

  async canCreateCommunity(): Promise<{ can: boolean; followers: number }> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) return { can: false, followers: 0 };

      const { data: profile } = await supabase
        .from("profiles")
        .select("followers_count, is_admin, email")
        .eq("id", user.id)
        .maybeSingle();
      if (
        profile?.is_admin ||
        profile?.email === "siwonikuayomide123@gmail.com"
      )
        return { can: true, followers: profile?.followers_count || 0 };

      const followers = profile?.followers_count || 0;
      return { can: followers >= 1000, followers };
    }
    return { can: false, followers: 0 };
  },

  async auditUser(userId: string) {
    if (!supabase) return null;

    const { data: profile, error: pError } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    if (pError || !profile) return null;

    let fraudScore = 0;
    const logs: string[] = [];

    // Heuristic 1: View to follower ratio
    const reach = profile.total_views || 0;
    const followers = profile.followers_count || 0;
    if (reach > 1000 && followers < 10) {
      fraudScore += 40;
      logs.push(
        "Suspiciously high reach compared to followers (Bot-like interaction detected)",
      );
    }

    // Heuristic 2: Conversion rate (Views to Likes/Saves)
    // We would need to fetch posts here for a deeper dive, let's stick to global stats for now

    // Heuristic 3: Rapid earnings growth
    if ((profile.wallet_balance || 0) > 50000 && followers < 100) {
      fraudScore += 30;
      logs.push("High earnings with minimal social validation");
    }

    if (fraudScore >= 70) {
      await supabase
        .from("profiles")
        .update({
          is_flagged: true,
          fraud_score: fraudScore,
          last_audit_date: new Date().toISOString(),
          audit_logs: logs,
        })
        .eq("id", userId);
    } else {
      await supabase
        .from("profiles")
        .update({
          fraud_score: fraudScore,
          last_audit_date: new Date().toISOString(),
          audit_logs: logs,
        })
        .eq("id", userId);
    }

    return { fraudScore, logs };
  },

  async ensureGlobalCommunity(): Promise<void> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("is_admin, email")
        .eq("id", user.id)
        .maybeSingle();
      if (
        !profile?.is_admin &&
        profile?.email !== "siwonikuayomide123@gmail.com"
      )
        return;

      const { data: existing } = await supabase
        .from("communities")
        .select("id")
        .eq("name", "Literally Official")
        .maybeSingle();

      if (!existing) {
        const { data: community, error } = await supabase
          .from("communities")
          .insert({
            name: "Literally Official",
            description:
              "The official community for all Literally users. Welcome home!",
            guidelines:
              "1. Be respectful to everyone.\n2. No spam or self-promotion.\n3. Share your love for reading and writing.\n4. Keep discussions constructive.\n5. Have fun!",
            is_private: false,
            creator_id: user.id,
            image_url:
              "https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&q=80&w=1000",
          })
          .select()
          .maybeSingle();

        if (community) {
          await supabase.from("community_members").insert({
            community_id: community.id,
            user_id: user.id,
            role: "creator",
          });
        }
      }
    }
  },

  async getCommunityPosts(communityId: string): Promise<any[]> {
    if (supabase) {
      const { data } = await supabase
        .from("community_posts")
        .select("*, profiles(display_name, full_name, avatar_url)")
        .eq("community_id", communityId)
        .order("created_at", { ascending: false });

      return (data || []).map((p: any) => ({
        ...p,
        author_name: p.profiles
          ? formatAuthorName(p.profiles.full_name, p.profiles.display_name)
          : "Anonymous",
        author_avatar: p.profiles?.avatar_url,
      }));
    }
    return [];
  },

  async getCommunityChannels(communityId: string): Promise<any[]> {
    if (supabase) {
      const { data, error } = await supabase
        .from("community_channels")
        .select("*")
        .eq("community_id", communityId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data || [];
    }
    return [];
  },

  async createCommunityChannel(
    communityId: string,
    name: string,
    description?: string,
  ): Promise<any> {
    if (supabase) {
      const { data, error } = await supabase
        .from("community_channels")
        .insert({ community_id: communityId, name, description })
        .select()
        .maybeSingle();
      if (error) throw error;
      return data;
    }
    throw new Error("Supabase not configured");
  },

  async getChannelMessages(channelId: string): Promise<any[]> {
    if (supabase) {
      const { data, error } = await supabase
        .from("community_posts")
        .select(
          `
          *,
          profiles:user_id (display_name, avatar_url)
        `,
        )
        .eq("channel_id", channelId)
        .order("created_at", { ascending: true })
        .limit(100);
      if (error) throw error;
      return data || [];
    }
    return [];
  },

  async sendChannelMessage(
    communityId: string,
    channelId: string,
    content: string,
  ): Promise<any> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("community_posts")
        .insert({
          community_id: communityId,
          channel_id: channelId,
          user_id: user.id,
          content,
        })
        .select()
        .maybeSingle();

      if (error) throw error;
      return data;
    }
    throw new Error("Supabase not configured");
  },

  async createCommunityPost(
    communityId: string,
    title: string,
    content: string,
  ): Promise<any> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("community_posts")
        .insert({
          community_id: communityId,
          user_id: user.id,
          title,
          content,
        })
        .select()
        .maybeSingle();

      if (error) throw error;
      return data;
    }
    throw new Error("Supabase not configured");
  },

  // --- EXTERNAL BOOKS & COLLABORATIONS ---
  async importWebWork(work: any): Promise<any> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) throw new Error("Not authenticated");

      // Retrieve user's display name
      const { data: profile } = await supabase
        .from("profiles")
        .select("display_name, avatar_url")
        .eq("id", user.id)
        .maybeSingle();
      const authorName = (profile && profile.display_name) || "Imported Author";
      const authorAvatar = (profile && profile.avatar_url) || "";

      if (work.is_post) {
        // Insert as a new Post
        const newPost: any = {
          user_id: user.id,
          title: work.title,
          content: work.content || work.summary,
          theme: work.genre || "General",
          author_name:
            work.author && work.author !== "Unknown Author"
              ? `${work.author} (via Web)`
              : `${authorName} (via Web)`,
          author_avatar: authorAvatar,
          status: "published",
          type: work.genre?.toLowerCase().includes("poem")
            ? "poem"
            : work.genre?.toLowerCase().includes("essay")
              ? "essay"
              : "article",
        };

        const { data, error } = await supabase
          .from("posts")
          .insert(newPost)
          .select()
          .maybeSingle();
        if (error) {
          // Fallback for missing custom schema fields if any
          const fallbackPost = { ...newPost };
          delete (fallbackPost as any).type;
          const { data: retryData, error: retryError } = await supabase
            .from("posts")
            .insert(fallbackPost)
            .select()
            .maybeSingle();
          if (retryError) throw retryError;
          return { type: "post", data: retryData };
        }
        return { type: "post", data };
      } else {
        // Insert as a new Book
        const newBook = {
          user_id: user.id,
          title: work.title,
          summary: work.summary,
          genre: work.genre || "General",
          mood: work.mood || "Neutral",
          lit_isbn: `WEB-${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
          cover_url:
            work.cover_url ||
            `https://images.unsplash.com/photo-1543002588-bfa74002ed7e?w=800&auto=format&fit=crop&q=80`,
          author_name:
            work.author && work.author !== "Unknown Author"
              ? `${work.author} (via Web)`
              : `${authorName} (via Web)`,
          is_published: true,
        };

        const { data: bookData, error: bookError } = await supabase
          .from("books")
          .insert(newBook)
          .select()
          .maybeSingle();
        if (bookError) throw bookError;

        // Create Chapters
        if (work.chapters && work.chapters.length > 0) {
          const chaptersToInsert = work.chapters.map(
            (ch: any, idx: number) => ({
              book_id: bookData.id,
              title: ch.title || `Chapter ${idx + 1}`,
              content: ch.content || "",
              chapter_number: idx + 1,
            }),
          );

          const { error: chError } = await supabase
            .from("chapters")
            .insert(chaptersToInsert);
          if (chError) {
            console.error("Error importing chapters:", chError);
          }
        } else {
          // Create at least one chapter containing the content
          const { error: chError } = await supabase.from("chapters").insert({
            book_id: bookData.id,
            title: "Introduction",
            content: work.summary || "Scraped open work content placeholder.",
            chapter_number: 1,
          });
          if (chError)
            console.error("Error creating default chapter:", chError);
        }

        return { type: "book", data: bookData };
      }
    }
    throw new Error("Supabase is not configured.");
  },

  async importExternalBook(book: any): Promise<any> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) throw new Error("Not authenticated");

      // Check if already exists
      if (book.external_id) {
        const { data: existing } = await supabase
          .from("external_books")
          .select("*")
          .eq("external_id", book.external_id)
          .maybeSingle();

        if (existing) return existing;
      }

      const { data, error } = await supabase
        .from("external_books")
        .insert({
          ...book,
          imported_by: user.id,
        })
        .select()
        .maybeSingle();

      if (error) throw error;
      return data;
    }
    throw new Error("Supabase not configured");
  },

  async getExternalBooks(): Promise<any[]> {
    if (supabase) {
      const { data } = await supabase
        .from("external_books")
        .select("*")
        .order("created_at", { ascending: false });
      return data || [];
    }
    return [];
  },

  async requestCollaboration(payload: {
    book_id?: string;
    external_book_id?: string;
    message: string;
    publisher_id?: string;
  }): Promise<any> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("collaborations")
        .insert({
          ...payload,
          requester_id: user.id,
        })
        .select()
        .maybeSingle();

      if (error) throw error;
      return data;
    }
    throw new Error("Supabase not configured");
  },

  async getMyCollaborations(): Promise<any[]> {
    if (supabase) {
      const user = await getCurrentUserInternal();
      if (!user) return [];

      const { data } = await supabase
        .from("collaborations")
        .select("*, books(*), external_books(*)")
        .or(`requester_id.eq.${user.id},publisher_id.eq.${user.id}`)
        .order("created_at", { ascending: false });

      return data || [];
    }
    return [];
  },
};
